/**
 * ADN — Lightweight site-wide i18n (no external deps).
 *
 * Usage:
 *   import { useI18n } from "@/lib/i18n";
 *   const { t, lang, setLang, LANGS } = useI18n();
 *   <h1>{t("hero.title.line1")}</h1>
 *
 * Auto-detects `navigator.language` on first visit, persists choice to
 * localStorage, and emits a `adn-lang-change` window event so other
 * components (e.g. SupportChat) can re-render.
 */
import { useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "adn_site_lang";
const EVENT_NAME = "adn-lang-change";

// Languages displayed in the picker (matches Concierge support)
export const LANGS = [
    { code: "en", short: "EN", name: "English" },
    { code: "es", short: "ES", name: "Español" },
    { code: "pt", short: "PT", name: "Português" },
    { code: "fr", short: "FR", name: "Français" },
    { code: "ht", short: "HT", name: "Kreyòl ayisyen" },
];

const RESOURCES = {
    en: {
        // Nav
        "nav.videos": "250 Years Gallery",
        "nav.offers": "Local Offers",
        "nav.marketplace": "App Marketplace",
        "nav.partners": "Partners",
        "nav.signin": "Sign In",
        "nav.join": "Join as Founding VIP",
        "nav.dashboard": "Dashboard",
        "nav.developer": "Developer",
        "nav.admin": "Admin",
        "nav.signout": "Sign Out",

        // Hero
        "hero.eyebrow": "July 4th, 2026 · Celebration of 250 Years™",
        "hero.title.line1": "Join Once.",
        "hero.title.line2a": "Save Forever.",
        "hero.title.line2b": "Discover More.",
        "hero.subhead.before": "Become a",
        "hero.subhead.role": "Founding VIP Member",
        "hero.subhead.middle": "for a one-time $25 activation and lock in",
        "hero.subhead.price": "$9.99/month for life",
        "hero.subhead.tail": "Receive a $100 hotel voucher, local savings, a community video showcase, and access to a vetted local app marketplace.",
        "hero.cta.join": "Become a Founding VIP",
        "hero.cta.videos": "Watch Celebration Videos",
        "hero.badge.secure": "Secure membership",
        "hero.badge.voucher": "$100 Hotel voucher",
        "hero.badge.rate": "Lifetime locked rate",
        "hero.counter.footnote": "Founding VIP pricing is locked for the original member only and is non-transferable.",

        // Section headings
        "sec.benefits.title": "A community membership built to compound in value.",
        "sec.how.eyebrow": "How it works",
        "sec.how.title": "Three steps to a lifetime of local.",
        "sec.offers.eyebrow": "Local Offers",
        "sec.offers.title": "Hand-picked deals from Main Street.",
        "sec.marketplace.eyebrow": "App Marketplace",
        "sec.marketplace.title": "Niche apps built by ADN Certified Developers.",
        "sec.partners.eyebrow": "Partners",
        "sec.partners.title": "First 100 businesses. Locked for life.",
        "sec.partners.biz": "Business Partners",
        "sec.partners.biz.cta": "Become a Business Partner",
        "sec.partners.dev": "App Developers",
        "sec.voucher.eyebrow": "Hotel Voucher",
        "sec.voucher.title": "Your $100 Hotel Voucher, on us.",
        "sec.voucher.body": "Activate your Founding VIP membership and receive a unique $100 hotel voucher code in your member dashboard. Redemption subject to partner terms, blackout dates, and minimum stays.",
        "sec.voucher.badge.eyebrow": "Founding VIP Reward",
        "sec.voucher.badge.value": "$100 Hotel Voucher",

        // FAQ
        "faq.eyebrow": "Frequently asked",
        "faq.title": "Everything you might want to know.",

        // Final CTA
        "cta.title": "Founding VIP pricing won't last forever.",

        // Footer columns
        "footer.tagline": "Local Rewards & Loyalty Network™. Celebrating community, commerce, and 250 years of American spirit.",
        "footer.col.platform": "Platform",
        "footer.col.legal": "Legal",
        "footer.col.compliance": "Compliance",

        // Founding counter widget (hero card)
        "counter.title": "Founding VIP Memberships",
        "counter.limit": "Limited to 50,000",
        "counter.claimed": "claimed",
        "counter.remaining": "memberships remaining",
        "counter.lock": "Lock in $9.99/mo for life",

        // Auth — login
        "login.eyebrow": "Sign in",
        "login.title": "Welcome back to ADN",
        "login.email": "Email",
        "login.password": "Password",
        "login.submit": "Sign In",
        "login.submitting": "Signing in…",
        "login.forgot": "Forgot password?",
        "login.no_account": "Don't have an account?",
        "login.create": "Create one",
        // Forgot password
        "forgot.title": "Forgot your password?",
        "forgot.subtitle": "Enter the email tied to your ADN account and we'll send you a secure reset link.",
        "forgot.email": "Email address",
        "forgot.submit": "Email me a reset link",
        "forgot.submitting": "Sending link…",
        "forgot.sent_title": "Check your inbox",
        "forgot.sent_msg": "If that matches an ADN account, a reset link is on its way. The link expires in 30 minutes.",
        "forgot.sent_spam": "Didn't receive it? Check your spam folder, or try again in a few minutes.",
        "forgot.back": "Back to sign in",
        // Reset password
        "reset.title": "Choose a new password",
        "reset.subtitle": "Your reset link is valid for 30 minutes. Pick a strong password — at least 8 characters.",
        "reset.password": "New password",
        "reset.confirm": "Confirm password",
        "reset.submit": "Update password",
        "reset.submitting": "Updating password…",
        "reset.success": "Password updated. Sign in with your new password.",
        "reset.err.missing": "Reset token is missing. Please use the link from your email.",
        "reset.err.short": "Password must be at least 8 characters.",
        "reset.err.match": "Passwords don't match.",
        "reset.err.fail": "Reset failed. The link may have expired.",

        // Auth — register
        "reg.title": "Join Once. Save Forever.",
        "reg.step1": "1. Create account",
        "reg.step2": "2. Activation",
        "reg.step3": "3. Welcome",
        "reg.first": "First name",
        "reg.last": "Last name",
        "reg.email": "Email",
        "reg.password": "Password (min 8 characters)",
        "reg.have_account": "Already have an account?",
        "reg.signin": "Sign in",

        // Founding VIP Drive countdown (above-hero)
        "countdown.eyebrow": "Founding VIP Drive · Ends",
        "countdown.eyebrow_expired": "Founding VIP Drive · Closed",
        "countdown.tagline.before": "Lock in your",
        "countdown.tagline.rate": "$9.99/mo Founding rate",
        "countdown.tagline.after": "before the clock runs out.",
        "countdown.tagline.expired": "Standard pricing now in effect",
        "countdown.label.days": "Days",
        "countdown.label.hours": "Hrs",
        "countdown.label.mins": "Min",
        "countdown.label.secs": "Sec",
        "countdown.progress.launched": "Drive launched",
        "countdown.progress.complete": "% complete",
        "countdown.progress.ends": "Drive ends",
        "countdown.expired_badge": "Drive Ended",

        // July 4th video section
        "july4.eyebrow": "Featured · July 4th, 2026",
        "july4.title": "Celebrate 250 Years With Your Community",
        "july4.subtitle": "Local voices, families, businesses, churches, veterans, youth, and community partners celebrating the July 4th 250-Year Celebration.",
        "july4.cta.watch": "Watch Celebration Videos",
        "july4.cta.submit": "Submit Your Video",

        // PWA install prompt
        "install.title": "Install ADN Rewards",
        "install.body": "Get the full-screen app on your home screen — faster access to vouchers, offers, and the AI concierge.",
        "install.body_ios_a": "Tap",
        "install.body_ios_b": "Share",
        "install.body_ios_c": "then",
        "install.body_ios_d": "Add to Home Screen",
        "install.body_ios_e": "for the full app experience.",
        "install.cta": "Install App",

        // Common UI
        "common.loading": "Loading…",
        "common.searching": "Searching…",
        "common.signin_to": "Sign in to",
        "common.clear": "Clear",
        "common.all": "All",
        "common.cancel": "Cancel",
        "common.save": "Save",
        "common.close": "Close",

        // Cookie banner
        "cookies.title": "We use a few cookies.",
        "cookies.body": "Strictly-necessary cookies keep you signed in. Optional ones help us improve ADN. See our",
        "cookies.privacy_link": "Privacy Policy",
        "cookies.accept_all": "Accept all",
        "cookies.essential": "Essential only",

        // Founding counter
        "founding.heading": "Founding VIP Memberships",
        "founding.limit": "Limited to 50,000",
        "founding.claimed": "claimed",
        "founding.available": "memberships available",
        "founding.lock_rate": "Lock $9.99/mo for life",
        "founding.disclaimer": "Founding VIP pricing is locked only for the original member and is non-transferable.",

        // Offers page
        "offers.eyebrow": "Member savings · Jacksonville, FL",
        "offers.title": "Local Offers",
        "offers.subtitle": "Hand-picked deals from Jacksonville businesses across every neighborhood — from Riverside to the Beaches.",
        "offers.search.placeholder": "Search by business name, neighborhood, zip code, category…",
        "offers.search.hint": "Search is case-insensitive — try \"riverside\", \"32250\", \"BBQ\", or \"San Marco\".",
        "offers.filter.all_neighborhoods": "All neighborhoods",
        "offers.filter.locating": "Locating…",
        "offers.filter.use_location": "Use my location",
        "offers.filter.sorted_by_distance": "Sorted by distance · Clear",
        "offers.filter.show_map": "Show map",
        "offers.filter.hide_map": "Hide map",
        "offers.filter.clear": "Clear filters",
        "offers.count.one": "offer found",
        "offers.count.many": "offers found",
        "offers.empty.title": "No offers match your filters",
        "offers.empty.body": "Try a different neighborhood, a zip code, or clear filters.",
        "offers.empty.cta": "Clear filters",
        "offers.card.distance_suffix": "mi away",
        "offers.card.250years": "250 Years",
        "offers.card.redeem": "Redeem",
        "offers.card.signin_to_redeem": "Sign in as an active member to redeem.",
        "offers.card.unlock_vip": "Reserve VIP $5/mo",
        "offers.card.join_vip_to_redeem": "Reserve VIP $5/mo",
        "offers.card.vip_only_pricing": "Everything's free through 12/4/2026 — reserve $5/mo VIP now.",
        "offers.banner.eyebrow": "Founding VIP · Pre-launch",
        "offers.banner.title": "Everything's free through Dec 4, 2026 — lock in $5/mo VIP.",
        "offers.banner.subtitle": "No payment now. Pre-register your name, email, phone, and ZIP to get an activation link the moment we unlock on 12/4/2026.",
        "offers.banner.cta": "Reserve $5/mo VIP",
        "offers.toast.location_set": "Location set — sorting by distance.",
        "offers.toast.location_unsupported": "Geolocation is not supported by your browser.",
        "offers.toast.location_fail": "Could not get your location.",
        "offers.toast.offer_saved": "Offer saved",

        // Marketplace page
        "marketplace.eyebrow": "ADN Certified Developers",
        "marketplace.title": "App Marketplace",
        "marketplace.subtitle": "Vetted, locally-focused apps — from babysitter networks to bookkeeping to church hubs.",
        "marketplace.loading": "Loading apps…",
        "marketplace.empty": "No apps in this category yet.",
        "marketplace.by": "by",
        "marketplace.install": "Install",
        "marketplace.subscribe": "Subscribe",
        "marketplace.buy": "Buy",
        "marketplace.signin_to_purchase": "Sign in to purchase.",

        // Footer
        "footer.col.terms": "Terms",
        "footer.col.privacy": "Privacy",
        "footer.col.voucher_terms": "Hotel Voucher Terms",
        "footer.col.video_terms": "Video Submission Terms",
        "footer.col.referral_terms": "Referral Program Terms",
        "footer.copyright": "All rights reserved.",

        // Settings page
        "settings.title": "Make it yours.",
        "settings.eyebrow": "App Settings",
        "settings.subtitle": "Language, voice, accessibility, updates. Everything saves automatically and follows you between visits.",
        "settings.section.language": "App Language",
        "settings.section.language.sub": "Used across the app and the AI Concierge.",
        "settings.section.updates": "Updates & App Version",
        "settings.section.concierge": "AI Concierge",
        "settings.section.concierge.sub": "How Ask ADN talks back to you.",
        "settings.section.notif": "Notifications",
        "settings.section.notif.sub": "How ADN keeps you informed.",
        "settings.section.display": "Display & Accessibility",
        "settings.section.display.sub": "Comfort and motion.",
        "settings.section.install": "Install & App",
        "settings.section.install.sub": "Get ADN on your home screen.",
        "settings.row.autodetect": "Auto-detect language on first visit",
        "settings.row.autodetect.hint": "When on, ADN uses your device's preferred language the very first time. After you pick one above, your choice always wins.",
        "settings.row.check_updates": "Check for updates now",
        "settings.row.check_updates.hint": "Clears the cached files, fetches the latest version, and reloads the app.",
        "settings.row.check_updates.cta": "Check for updates",
        "settings.row.check_updates.busy": "Refreshing…",
        "settings.row.autospeak": "Read replies aloud",
        "settings.row.autospeak.hint": "The Concierge speaks each reply using your device voice.",
        "settings.row.tts_rate": "Reading speed",
        "settings.row.tts_rate.hint": "How fast replies are read out loud.",
        "settings.row.simple_mode": "Simple mode",
        "settings.row.simple_mode.hint": "Plain-language replies — short sentences, no jargon.",
        "settings.row.email_transcripts": "Email me my chat transcripts when I end a conversation",
        "settings.row.clear_chat": "Clear chat history on this device",
        "settings.row.clear_chat.hint": "Starts a brand-new conversation. Your past transcripts remain in your account.",
        "settings.row.clear_chat.cta": "Clear chat",
        "settings.row.browser_notif": "Browser notifications",
        "settings.row.browser_notif.hint": "Get a quiet ping when a new local offer drops or your voucher window opens. Requires your permission.",
        "settings.row.browser_notif.enabled": "Enabled",
        "settings.row.browser_notif.enable": "Enable",
        "settings.row.reduce_motion": "Reduce motion",
        "settings.row.reduce_motion.hint": "Disables fade-ins, parallax, and decorative animations.",
        "settings.row.larger_text": "Larger text",
        "settings.row.larger_text.hint": "Increase base font size by 12% across the app.",
        "settings.row.install_status": "Installation status",
        "settings.row.install_status.installed": "ADN Rewards is installed as an app on this device.",
        "settings.row.install_status.browser": "You're currently using ADN in a browser tab. Install for a faster, fullscreen experience.",
        "settings.row.install_status.badge_installed": "Installed",
        "settings.row.install_status.badge_browser": "Browser tab",
        "settings.row.reset_install": "Show install prompt again",
        "settings.row.reset_install.hint": "If you dismissed the install card earlier, this brings it back next visit.",
        "settings.row.reset_install.cta": "Reset prompt",
        "settings.tts.slow": "Slow",
        "settings.tts.normal": "Normal",
        "settings.tts.fast": "Fast",
        "settings.privacy_center": "Privacy Center",
        "settings.terms_of_service": "Terms of Service",
        "settings.notif.disclaimer": "We never send marketing pushes. Notifications are reserved for account events (voucher availability, referral approval) and local-offer alerts you opt into.",
        "settings.toast.lang_updated": "Language updated",
        "settings.toast.updates_checking": "Checking for updates…",
        "settings.toast.updates_checking.sub": "The app will reload in a moment.",
        "settings.toast.chat_cleared": "Chat history cleared on this device",
        "settings.toast.install_reset": "Install prompt will appear again on next visit",
        "settings.toast.notif_enabled": "Notifications enabled",
        "settings.toast.notif_not_enabled": "Notifications were not enabled",
        "settings.toast.notif_not_enabled.sub": "You can change this in your browser settings.",
        "settings.toast.notif_unsupported": "Notifications are not supported on this device",
        "settings.confirm.clear_chat": "End the current conversation and clear chat history on this device?",

        // Member Dashboard (hero + status)
        "member.eyebrow": "Member Dashboard",
        "member.welcome": "Welcome,",
        "member.member_id": "Member ID:",
        "member.badge.founding": "Founding VIP",
        "member.badge.lifetime": "Lifetime $9.99/mo",
        "member.badge.active": "Active",
        "member.badge.inactive": "Inactive",
        "member.cta.activate": "Activate Membership ($25)",
        "member.cta.settings": "Settings",

        // FAQ accordion (17 items) — Q/A pairs. Brand terms intentionally English.
        "faq.q1": "Is this an investment?",
        "faq.a1": "No. ADN Local Rewards & Loyalty Network™ is a paid membership and rewards platform. There is no ownership, profit sharing, securities, equity, or passive income offering.",
        "faq.q2": "What does $25 cover?",
        "faq.a2": "The $25 one-time Founding VIP activation reserves your lifetime $9.99/month rate, kicks off your benefits, and issues your $100 hotel voucher.",
        "faq.q3": "Can I transfer my membership?",
        "faq.a3": "No. Founding VIP pricing is locked to the original member only and is non-transferable.",
        "faq.q4": "How long is Founding VIP pricing available?",
        "faq.a4": "Founding VIP pricing is reserved for the first 50,000 members. Once that cap is met, standard pricing applies. You can track the live counter on this page.",
        "faq.q5": "What happens if I cancel?",
        "faq.a5": "You can cancel anytime from your member dashboard and you'll keep full access for a 7-day grace period. Reactivate within those 7 days and nothing changes. After the grace period, your membership and any business, developer, or ambassador roles tied to it are paused.",
        "faq.q6": "What if my payment fails?",
        "faq.a6": "We don't suspend you immediately. You enter a grace window so you can update your card and continue uninterrupted. After the grace window, the Core ADN Rule pauses your business profile, app listings, and ambassador rewards until the membership is restored.",
        "faq.q7": "How does the hotel voucher work?",
        "faq.a7": "Once activated, you'll receive a unique $100 voucher code in your member dashboard. Redeem with approved partners per the voucher terms.",
        "faq.q8": "Who can submit a celebration video?",
        "faq.a8": "Active members may submit a video for review. All videos go through approval, must respect copyright, and follow the community video terms.",
        "faq.q9": "What is the Founding Business Partner offer?",
        "faq.a9": "The first 100 local businesses to join ADN get Founding Business Partner™ status — $0/month forever (normally $49/mo), priority placement on the city map, and one active offer locked in for life as long as their owner keeps an active ADN membership.",
        "faq.q10": "Why do business owners and developers need a membership?",
        "faq.a10": "ADN is a member-first community. The Core ADN Rule requires every business owner, certified developer, and ambassador to maintain an active ADN membership so that everyone listed on the platform is also participating in it.",
        "faq.q11": "What apps are in the marketplace?",
        "faq.a11": "Vetted, locally-focused apps from ADN Certified developers — across services, business tools, AI, education, real estate, church apps, and more.",
        "faq.q12": "How do I become an ADN Certified Developer?",
        "faq.a12": "Anyone can apply — stay-at-home parents, skilled or unskilled developers, content creators, influencers, students, retirees, physically challenged individuals, and anyone who has ever been denied access. Enroll in the 12-week ADN AI App Certification curriculum and learn all the skills necessary to become AI-Certified. Only 100 certifications are available at the limited-time launch price of $999 (regular price $4,999). Scholarships are available — apply through the Partners portal and tell us why you want this opportunity. After certification, submit your app for review by the App Review Team. Active ADN membership is required throughout.",
        "faq.q13": "How do referrals and the Ambassador program work?",
        "faq.a13": "Every active member gets a unique referral link. You earn rewards for each friend who activates, capped at 12 months of credits, and at 25 successful referrals your $9.99/month rate is upgraded to lifetime free for as long as you stay active.",
        "faq.q14": "Can I find offers near me?",
        "faq.a14": "Yes. On the Local Offers page, tap \"Use my location\" to sort businesses by distance using your ZIP or device location. You can also filter by neighborhood, category, or search by business name or ZIP.",
        "faq.q15": "Where does ADN operate?",
        "faq.a15": "We're launching in Jacksonville, FL alongside the July 4, 2026 America 250 celebration, with new founding cities being added based on member demand. The Outreach pipeline ranks under-served cities so we know where to expand next.",
        "faq.q16": "Is my information safe?",
        "faq.a16": "Yes. ADN uses encrypted authentication, never sells your personal data, and only shares the minimum information needed to redeem an offer with the business you're transacting with. Full details are in the Privacy policy.",
        "faq.q17": "How do I get help?",
        "faq.a17": "Reach support directly from your member dashboard or through the contact link in the footer. Founding VIP members get priority response.",
        "faq.q18": "What is ADN?",
        "faq.a18": "ADN — short for Axxess Denied Network — is a community impact marketplace based in Jacksonville. We help local businesses grow by connecting them directly with members who want to support their neighborhood, and we give community campaigns the visibility they need to thrive.",
        "faq.q19": "How do I earn ADN Credits?",
        "faq.a19": "Members earn ADN Credits every time they redeem a local offer, refer a friend, or contribute to a community campaign. Credits unlock deeper discounts, hotel vouchers, and member-only perks across the platform.",
        "faq.q20": "How can I support a local business?",
        "faq.a20": "Become a member, redeem their offers, share their profile with neighbors, and leave honest reviews. Every redemption sends real revenue directly to the Jacksonville business — no middleman markup.",
    },

    es: {
        "nav.videos": "Videos de 250 Años",
        "nav.offers": "Ofertas Locales",
        "nav.marketplace": "Mercado de Apps",
        "nav.partners": "Socios",
        "nav.signin": "Iniciar Sesión",
        "nav.join": "Únete como VIP Fundador",
        "nav.dashboard": "Panel",
        "nav.developer": "Desarrollador",
        "nav.admin": "Administrador",
        "nav.signout": "Cerrar Sesión",

        "hero.eyebrow": "4 de Julio de 2026 · Celebración de 250 Años™",
        "hero.title.line1": "Únete Una Vez.",
        "hero.title.line2a": "Ahorra Para Siempre.",
        "hero.title.line2b": "Descubre Más.",
        "hero.subhead.before": "Conviértete en",
        "hero.subhead.role": "Miembro VIP Fundador",
        "hero.subhead.middle": "por una activación única de $25 y bloquea",
        "hero.subhead.price": "$9.99/mes de por vida",
        "hero.subhead.tail": "Recibe un vale de hotel de $100, ahorros locales, una vitrina comunitaria de videos y acceso a un mercado curado de aplicaciones locales.",
        "hero.cta.join": "Únete como VIP Fundador",
        "hero.cta.videos": "Ver Videos de la Celebración",
        "hero.badge.secure": "Membresía segura",
        "hero.badge.voucher": "Vale de hotel de $100",
        "hero.badge.rate": "Tarifa vitalicia",
        "hero.counter.footnote": "El precio de VIP Fundador queda bloqueado solo para el miembro original y no es transferible.",

        "sec.benefits.title": "Una membresía comunitaria diseñada para aumentar de valor.",
        "sec.how.eyebrow": "Cómo funciona",
        "sec.how.title": "Tres pasos hacia una vida local.",
        "sec.offers.eyebrow": "Ofertas Locales",
        "sec.offers.title": "Ofertas seleccionadas de la calle principal.",
        "sec.marketplace.eyebrow": "Mercado de Apps",
        "sec.marketplace.title": "Apps especializadas creadas por Desarrolladores Certificados de ADN.",
        "sec.partners.eyebrow": "Socios",
        "sec.partners.title": "Primeros 100 negocios. Bloqueados de por vida.",
        "sec.partners.biz": "Socios Comerciales",
        "sec.partners.biz.cta": "Conviértete en Socio Comercial",
        "sec.partners.dev": "Desarrolladores de Apps",
        "sec.voucher.eyebrow": "Vale de Hotel",
        "sec.voucher.title": "Tu vale de hotel de $100, cortesía nuestra.",
        "sec.voucher.body": "Activa tu membresía VIP Fundador y recibe un código único de vale de hotel de $100 en tu panel de miembro. Canje sujeto a los términos del socio, fechas restringidas y estadías mínimas.",
        "sec.voucher.badge.eyebrow": "Recompensa VIP Fundador",
        "sec.voucher.badge.value": "Vale de Hotel de $100",

        "faq.eyebrow": "Preguntas frecuentes",
        "faq.title": "Todo lo que querrías saber.",

        "cta.title": "El precio de VIP Fundador no durará para siempre.",

        "footer.tagline": "Red de Recompensas y Lealtad Local™. Celebrando la comunidad, el comercio y 250 años del espíritu estadounidense.",
        "footer.col.platform": "Plataforma",
        "footer.col.legal": "Legal",
        "footer.col.compliance": "Cumplimiento",

        "counter.title": "Membresías VIP Fundador",
        "counter.limit": "Limitado a 50,000",
        "counter.claimed": "reclamadas",
        "counter.remaining": "membresías disponibles",
        "counter.lock": "Bloquea $9.99/mes de por vida",

        "login.eyebrow": "Iniciar sesión",
        "login.title": "Bienvenido de nuevo a ADN",
        "login.email": "Correo electrónico",
        "login.password": "Contraseña",
        "login.submit": "Iniciar Sesión",
        "login.submitting": "Iniciando sesión…",
        "login.forgot": "¿Olvidaste tu contraseña?",
        "login.no_account": "¿No tienes una cuenta?",
        "login.create": "Crear una",
        "forgot.title": "¿Olvidaste tu contraseña?",
        "forgot.subtitle": "Ingresa el correo asociado a tu cuenta ADN y te enviaremos un enlace seguro para restablecerla.",
        "forgot.email": "Correo electrónico",
        "forgot.submit": "Enviarme el enlace",
        "forgot.submitting": "Enviando enlace…",
        "forgot.sent_title": "Revisa tu bandeja de entrada",
        "forgot.sent_msg": "Si coincide con una cuenta ADN, el enlace está en camino. Expira en 30 minutos.",
        "forgot.sent_spam": "¿No lo recibiste? Revisa la carpeta de spam o intenta de nuevo en unos minutos.",
        "forgot.back": "Volver al inicio de sesión",
        "reset.title": "Elige una nueva contraseña",
        "reset.subtitle": "Tu enlace es válido por 30 minutos. Usa al menos 8 caracteres.",
        "reset.password": "Nueva contraseña",
        "reset.confirm": "Confirmar contraseña",
        "reset.submit": "Actualizar contraseña",
        "reset.submitting": "Actualizando…",
        "reset.success": "Contraseña actualizada. Inicia sesión con la nueva.",
        "reset.err.missing": "Falta el token. Usa el enlace de tu correo.",
        "reset.err.short": "Debe tener al menos 8 caracteres.",
        "reset.err.match": "Las contraseñas no coinciden.",
        "reset.err.fail": "No se pudo restablecer. El enlace pudo expirar.",

        "reg.title": "Únete Una Vez. Ahorra Para Siempre.",
        "reg.step1": "1. Crear cuenta",
        "reg.step2": "2. Activación",
        "reg.step3": "3. Bienvenida",
        "reg.first": "Nombre",
        "reg.last": "Apellido",
        "reg.email": "Correo electrónico",
        "reg.password": "Contraseña (mínimo 8 caracteres)",
        "reg.have_account": "¿Ya tienes una cuenta?",
        "reg.signin": "Iniciar sesión",

        "countdown.eyebrow": "Drive VIP Fundador · Termina",
        "countdown.eyebrow_expired": "Drive VIP Fundador · Cerrado",
        "countdown.tagline.before": "Bloquea tu",
        "countdown.tagline.rate": "tarifa Fundador de $9.99/mes",
        "countdown.tagline.after": "antes de que se acabe el tiempo.",
        "countdown.tagline.expired": "Precio estándar ahora en vigor",
        "countdown.label.days": "Días",
        "countdown.label.hours": "Hrs",
        "countdown.label.mins": "Min",
        "countdown.label.secs": "Seg",
        "countdown.progress.launched": "Drive iniciado",
        "countdown.progress.complete": "% completado",
        "countdown.progress.ends": "Drive termina",
        "countdown.expired_badge": "Drive Cerrado",

        "july4.eyebrow": "Destacado · 4 de Julio de 2026",
        "july4.title": "Celebra 250 Años Con Tu Comunidad",
        "july4.subtitle": "Voces locales, familias, negocios, iglesias, veteranos, jóvenes y socios comunitarios celebrando la Celebración del 4 de Julio de los 250 Años.",
        "july4.cta.watch": "Ver Videos de la Celebración",
        "july4.cta.submit": "Envía Tu Video",

        "install.title": "Instalar ADN Rewards",
        "install.body": "Obtén la app a pantalla completa en tu pantalla de inicio — acceso más rápido a vales, ofertas y al conserje de IA.",
        "install.body_ios_a": "Toca",
        "install.body_ios_b": "Compartir",
        "install.body_ios_c": "y luego",
        "install.body_ios_d": "Añadir a Pantalla de Inicio",
        "install.body_ios_e": "para la experiencia completa.",
        "install.cta": "Instalar App",

        "common.loading": "Cargando…",
        "common.searching": "Buscando…",
        "common.signin_to": "Inicia sesión para",
        "common.clear": "Limpiar",
        "common.all": "Todos",
        "common.cancel": "Cancelar",
        "common.save": "Guardar",
        "common.close": "Cerrar",

        "cookies.title": "Usamos algunas cookies.",
        "cookies.body": "Las cookies estrictamente necesarias te mantienen conectado. Las opcionales nos ayudan a mejorar ADN. Consulta nuestra",
        "cookies.privacy_link": "Política de Privacidad",
        "cookies.accept_all": "Aceptar todas",
        "cookies.essential": "Solo esenciales",

        "founding.heading": "Membresías Founding VIP",
        "founding.limit": "Limitado a 50,000",
        "founding.claimed": "reclamadas",
        "founding.available": "membresías disponibles",
        "founding.lock_rate": "Bloquea $9.99/mes de por vida",
        "founding.disclaimer": "El precio Founding VIP queda bloqueado solo para el miembro original y no es transferible.",

        "offers.eyebrow": "Ahorros para miembros · Jacksonville, FL",
        "offers.title": "Ofertas Locales",
        "offers.subtitle": "Ofertas seleccionadas de negocios de Jacksonville en cada vecindario — desde Riverside hasta las Playas.",
        "offers.search.placeholder": "Busca por nombre de negocio, vecindario, código postal, categoría…",
        "offers.search.hint": "La búsqueda no distingue mayúsculas — prueba \"riverside\", \"32250\", \"BBQ\" o \"San Marco\".",
        "offers.filter.all_neighborhoods": "Todos los vecindarios",
        "offers.filter.locating": "Localizando…",
        "offers.filter.use_location": "Usar mi ubicación",
        "offers.filter.sorted_by_distance": "Ordenado por distancia · Limpiar",
        "offers.filter.show_map": "Mostrar mapa",
        "offers.filter.hide_map": "Ocultar mapa",
        "offers.filter.clear": "Limpiar filtros",
        "offers.count.one": "oferta encontrada",
        "offers.count.many": "ofertas encontradas",
        "offers.empty.title": "Ninguna oferta coincide con tus filtros",
        "offers.empty.body": "Prueba otro vecindario, un código postal o limpia los filtros.",
        "offers.empty.cta": "Limpiar filtros",
        "offers.card.distance_suffix": "millas",
        "offers.card.250years": "250 Años",
        "offers.card.redeem": "Canjear",
        "offers.card.signin_to_redeem": "Inicia sesión como miembro activo para canjear.",
        "offers.card.unlock_vip": "Reserva VIP $5/mes",
        "offers.card.join_vip_to_redeem": "Reserva VIP $5/mes",
        "offers.card.vip_only_pricing": "Todo es gratis hasta el 4/12/2026 — reserva VIP a $5/mes.",
        "offers.banner.eyebrow": "Fundador VIP · Pre-lanzamiento",
        "offers.banner.title": "Todo es gratis hasta el 4 de dic. de 2026 — asegura VIP a $5/mes.",
        "offers.banner.subtitle": "Sin pago ahora. Pre-regístrate con nombre, correo, teléfono y código postal para recibir el enlace de activación el 4/12/2026.",
        "offers.banner.cta": "Reservar VIP $5/mes",
        "offers.toast.location_set": "Ubicación establecida — ordenando por distancia.",
        "offers.toast.location_unsupported": "Tu navegador no admite geolocalización.",
        "offers.toast.location_fail": "No se pudo obtener tu ubicación.",
        "offers.toast.offer_saved": "Oferta guardada",

        "marketplace.eyebrow": "Desarrolladores Certificados ADN",
        "marketplace.title": "Mercado de Apps",
        "marketplace.subtitle": "Apps verificadas con enfoque local — desde redes de niñeras hasta contabilidad y centros de iglesia.",
        "marketplace.loading": "Cargando apps…",
        "marketplace.empty": "Aún no hay apps en esta categoría.",
        "marketplace.by": "por",
        "marketplace.install": "Instalar",
        "marketplace.subscribe": "Suscribirse",
        "marketplace.buy": "Comprar",
        "marketplace.signin_to_purchase": "Inicia sesión para comprar.",

        "footer.col.terms": "Términos",
        "footer.col.privacy": "Privacidad",
        "footer.col.voucher_terms": "Términos del Vale de Hotel",
        "footer.col.video_terms": "Términos de Envío de Video",
        "footer.col.referral_terms": "Términos del Programa de Referidos",
        "footer.copyright": "Todos los derechos reservados.",

        "settings.title": "Hazlo tuyo.",
        "settings.eyebrow": "Configuración de la App",
        "settings.subtitle": "Idioma, voz, accesibilidad, actualizaciones. Todo se guarda automáticamente y te sigue entre visitas.",
        "settings.section.language": "Idioma de la App",
        "settings.section.language.sub": "Se usa en toda la app y en el AI Concierge.",
        "settings.section.updates": "Actualizaciones y Versión",
        "settings.section.concierge": "AI Concierge",
        "settings.section.concierge.sub": "Cómo te responde Ask ADN.",
        "settings.section.notif": "Notificaciones",
        "settings.section.notif.sub": "Cómo ADN te mantiene informado.",
        "settings.section.display": "Pantalla y Accesibilidad",
        "settings.section.display.sub": "Comodidad y movimiento.",
        "settings.section.install": "Instalar y App",
        "settings.section.install.sub": "Obtén ADN en tu pantalla de inicio.",
        "settings.row.autodetect": "Detectar idioma en la primera visita",
        "settings.row.autodetect.hint": "Cuando está activo, ADN usa el idioma preferido de tu dispositivo la primera vez. Después de elegir uno arriba, tu elección siempre gana.",
        "settings.row.check_updates": "Buscar actualizaciones ahora",
        "settings.row.check_updates.hint": "Limpia los archivos en caché, descarga la última versión y recarga la app.",
        "settings.row.check_updates.cta": "Buscar actualizaciones",
        "settings.row.check_updates.busy": "Actualizando…",
        "settings.row.autospeak": "Leer respuestas en voz alta",
        "settings.row.autospeak.hint": "El Concierge habla cada respuesta usando la voz de tu dispositivo.",
        "settings.row.tts_rate": "Velocidad de lectura",
        "settings.row.tts_rate.hint": "Qué tan rápido se leen las respuestas en voz alta.",
        "settings.row.simple_mode": "Modo simple",
        "settings.row.simple_mode.hint": "Respuestas en lenguaje sencillo — frases cortas, sin jerga.",
        "settings.row.email_transcripts": "Enviarme transcripciones del chat al terminar una conversación",
        "settings.row.clear_chat": "Borrar historial de chat en este dispositivo",
        "settings.row.clear_chat.hint": "Comienza una conversación nueva. Tus transcripciones pasadas permanecen en tu cuenta.",
        "settings.row.clear_chat.cta": "Borrar chat",
        "settings.row.browser_notif": "Notificaciones del navegador",
        "settings.row.browser_notif.hint": "Recibe un aviso discreto cuando aparezca una nueva oferta local o se abra tu ventana de vale. Requiere tu permiso.",
        "settings.row.browser_notif.enabled": "Activado",
        "settings.row.browser_notif.enable": "Activar",
        "settings.row.reduce_motion": "Reducir movimiento",
        "settings.row.reduce_motion.hint": "Desactiva los desvanecimientos, el paralaje y las animaciones decorativas.",
        "settings.row.larger_text": "Texto más grande",
        "settings.row.larger_text.hint": "Aumenta el tamaño base de la fuente un 12% en toda la app.",
        "settings.row.install_status": "Estado de instalación",
        "settings.row.install_status.installed": "ADN Rewards está instalado como app en este dispositivo.",
        "settings.row.install_status.browser": "Estás usando ADN en una pestaña del navegador. Instala para una experiencia más rápida y a pantalla completa.",
        "settings.row.install_status.badge_installed": "Instalado",
        "settings.row.install_status.badge_browser": "Navegador",
        "settings.row.reset_install": "Mostrar el aviso de instalación nuevamente",
        "settings.row.reset_install.hint": "Si descartaste el aviso antes, esto lo trae de vuelta en tu próxima visita.",
        "settings.row.reset_install.cta": "Restablecer aviso",
        "settings.tts.slow": "Lenta",
        "settings.tts.normal": "Normal",
        "settings.tts.fast": "Rápida",
        "settings.privacy_center": "Centro de Privacidad",
        "settings.terms_of_service": "Términos del Servicio",
        "settings.notif.disclaimer": "Nunca enviamos avisos publicitarios. Las notificaciones están reservadas para eventos de cuenta (disponibilidad de vales, aprobación de referidos) y alertas de ofertas locales a las que te suscribas.",
        "settings.toast.lang_updated": "Idioma actualizado",
        "settings.toast.updates_checking": "Buscando actualizaciones…",
        "settings.toast.updates_checking.sub": "La app se recargará en un momento.",
        "settings.toast.chat_cleared": "Historial de chat borrado en este dispositivo",
        "settings.toast.install_reset": "El aviso de instalación volverá a aparecer en tu próxima visita",
        "settings.toast.notif_enabled": "Notificaciones activadas",
        "settings.toast.notif_not_enabled": "Las notificaciones no se activaron",
        "settings.toast.notif_not_enabled.sub": "Puedes cambiar esto en la configuración de tu navegador.",
        "settings.toast.notif_unsupported": "Las notificaciones no son compatibles con este dispositivo",
        "settings.confirm.clear_chat": "¿Terminar la conversación actual y borrar el historial de chat en este dispositivo?",

        "member.eyebrow": "Panel del Miembro",
        "member.welcome": "Bienvenido,",
        "member.member_id": "ID del Miembro:",
        "member.badge.founding": "Founding VIP",
        "member.badge.lifetime": "Vitalicia $9.99/mes",
        "member.badge.active": "Activa",
        "member.badge.inactive": "Inactiva",
        "member.cta.activate": "Activar Membresía ($25)",
        "member.cta.settings": "Configuración",

        "faq.q1": "¿Esto es una inversión?",
        "faq.a1": "No. ADN Local Rewards & Loyalty Network™ es una plataforma de membresía y recompensas pagada. No hay propiedad, participación en ganancias, valores, capital ni ingresos pasivos.",
        "faq.q2": "¿Qué cubre los $25?",
        "faq.a2": "La activación única Founding VIP de $25 reserva tu tarifa vitalicia de $9.99/mes, inicia tus beneficios y emite tu vale de hotel de $100.",
        "faq.q3": "¿Puedo transferir mi membresía?",
        "faq.a3": "No. El precio Founding VIP está bloqueado solo para el miembro original y no es transferible.",
        "faq.q4": "¿Cuánto tiempo está disponible el precio Founding VIP?",
        "faq.a4": "El precio Founding VIP está reservado para los primeros 50,000 miembros. Una vez alcanzado ese límite, se aplica el precio estándar. Puedes ver el contador en vivo en esta página.",
        "faq.q5": "¿Qué pasa si cancelo?",
        "faq.a5": "Puedes cancelar en cualquier momento desde tu panel de miembro y mantendrás acceso completo durante un período de gracia de 7 días. Reactiva dentro de esos 7 días y nada cambia. Después del período de gracia, tu membresía y cualquier rol de negocio, desarrollador o embajador vinculado a ella se pausa.",
        "faq.q6": "¿Qué pasa si mi pago falla?",
        "faq.a6": "No te suspendemos inmediatamente. Entras en un período de gracia para actualizar tu tarjeta y continuar sin interrupción. Después del período de gracia, la Core ADN Rule pausa tu perfil de negocio, listados de apps y recompensas de embajador hasta que se restaure la membresía.",
        "faq.q7": "¿Cómo funciona el vale de hotel?",
        "faq.a7": "Una vez activado, recibirás un código de vale único de $100 en tu panel de miembro. Canjéalo con socios aprobados según los términos del vale.",
        "faq.q8": "¿Quién puede enviar un video de celebración?",
        "faq.a8": "Los miembros activos pueden enviar un video para revisión. Todos los videos pasan por aprobación, deben respetar los derechos de autor y seguir los términos de video de la comunidad.",
        "faq.q9": "¿Cuál es la oferta Founding Business Partner?",
        "faq.a9": "Los primeros 100 negocios locales que se unan a ADN obtienen el estado de Founding Business Partner™ — $0/mes para siempre (normalmente $49/mes), ubicación prioritaria en el mapa de la ciudad y una oferta activa bloqueada de por vida mientras su propietario mantenga una membresía ADN activa.",
        "faq.q10": "¿Por qué los dueños de negocios y desarrolladores necesitan una membresía?",
        "faq.a10": "ADN es una comunidad primero para miembros. La Core ADN Rule requiere que cada dueño de negocio, desarrollador certificado y embajador mantenga una membresía ADN activa para que todos los listados en la plataforma también participen en ella.",
        "faq.q11": "¿Qué apps hay en el mercado?",
        "faq.a11": "Apps verificadas con enfoque local de desarrolladores ADN Certified — servicios, herramientas de negocio, IA, educación, bienes raíces, apps de iglesia y más.",
        "faq.q12": "¿Cómo me convierto en un ADN Certified Developer?",
        "faq.a12": "Cualquiera puede aplicar — padres que se quedan en casa, desarrolladores con o sin experiencia, creadores de contenido, influencers, estudiantes, jubilados, personas con discapacidades físicas y cualquiera que haya sido negado el acceso. Inscríbete en el currículo de 12 semanas ADN AI App Certification y aprende todas las habilidades necesarias para ser AI-Certified. Solo hay 100 certificaciones disponibles al precio de lanzamiento por tiempo limitado de $999 (precio regular $4,999). Hay becas disponibles — aplica a través del portal de Socios y cuéntanos por qué quieres esta oportunidad. Después de la certificación, envía tu app para revisión por el App Review Team. Se requiere membresía ADN activa durante todo el proceso.",
        "faq.q13": "¿Cómo funcionan los referidos y el programa de Embajadores?",
        "faq.a13": "Cada miembro activo recibe un enlace de referido único. Ganas recompensas por cada amigo que se active, hasta 12 meses de créditos, y con 25 referidos exitosos tu tarifa de $9.99/mes se actualiza a vitalicia gratis mientras te mantengas activo.",
        "faq.q14": "¿Puedo encontrar ofertas cerca de mí?",
        "faq.a14": "Sí. En la página de Ofertas Locales, toca \"Usar mi ubicación\" para ordenar los negocios por distancia usando tu código postal o ubicación del dispositivo. También puedes filtrar por vecindario, categoría o buscar por nombre de negocio o código postal.",
        "faq.q15": "¿Dónde opera ADN?",
        "faq.a15": "Estamos lanzando en Jacksonville, FL junto con la celebración del 4 de julio de 2026 de America 250, con nuevas ciudades fundadoras agregadas según la demanda de los miembros. La canalización de Outreach clasifica las ciudades desatendidas para saber dónde expandirnos a continuación.",
        "faq.q16": "¿Mi información está segura?",
        "faq.a16": "Sí. ADN usa autenticación cifrada, nunca vende tus datos personales y solo comparte la información mínima necesaria para canjear una oferta con el negocio con el que estás haciendo la transacción. Detalles completos en la política de Privacidad.",
        "faq.q17": "¿Cómo obtengo ayuda?",
        "faq.a17": "Contacta a soporte directamente desde tu panel de miembro o a través del enlace de contacto en el pie de página. Los miembros Founding VIP reciben respuesta prioritaria.",
        "faq.q18": "¿Qué es ADN?",
        "faq.a18": "ADN — abreviatura de Axxess Denied Network — es un mercado de impacto comunitario en Jacksonville. Ayudamos a los negocios locales a crecer conectándolos directamente con miembros que quieren apoyar a su vecindario, y damos visibilidad a las campañas comunitarias.",
        "faq.q19": "¿Cómo gano créditos ADN?",
        "faq.a19": "Los miembros ganan créditos ADN cada vez que canjean una oferta local, refieren a un amigo o contribuyen a una campaña comunitaria. Los créditos desbloquean descuentos adicionales, vales de hotel y beneficios exclusivos.",
        "faq.q20": "¿Cómo puedo apoyar a un negocio local?",
        "faq.a20": "Hazte miembro, canjea sus ofertas, comparte su perfil con vecinos y deja reseñas honestas. Cada canje envía ingresos reales directamente al negocio de Jacksonville — sin intermediarios.",
    },

    pt: {
        "nav.videos": "Vídeos dos 250 Anos",
        "nav.offers": "Ofertas Locais",
        "nav.marketplace": "Mercado de Apps",
        "nav.partners": "Parceiros",
        "nav.signin": "Entrar",
        "nav.join": "Junte-se como VIP Fundador",
        "nav.dashboard": "Painel",
        "nav.developer": "Desenvolvedor",
        "nav.admin": "Administrador",
        "nav.signout": "Sair",

        "hero.eyebrow": "4 de Julho de 2026 · Celebração dos 250 Anos™",
        "hero.title.line1": "Junte-se Uma Vez.",
        "hero.title.line2a": "Economize Para Sempre.",
        "hero.title.line2b": "Descubra Mais.",
        "hero.subhead.before": "Torne-se um",
        "hero.subhead.role": "Membro VIP Fundador",
        "hero.subhead.middle": "por uma ativação única de $25 e garanta",
        "hero.subhead.price": "$9.99/mês para sempre",
        "hero.subhead.tail": "Receba um voucher de hotel de $100, economias locais, uma vitrine comunitária de vídeos e acesso a um mercado curado de aplicativos locais.",
        "hero.cta.join": "Junte-se como VIP Fundador",
        "hero.cta.videos": "Assistir Vídeos da Celebração",
        "hero.badge.secure": "Associação segura",
        "hero.badge.voucher": "Voucher de hotel de $100",
        "hero.badge.rate": "Tarifa vitalícia",
        "hero.counter.footnote": "O preço VIP Fundador está bloqueado apenas para o membro original e não é transferível.",

        "sec.benefits.title": "Uma associação comunitária criada para valorizar com o tempo.",
        "sec.how.eyebrow": "Como funciona",
        "sec.how.title": "Três passos para uma vida local.",
        "sec.offers.eyebrow": "Ofertas Locais",
        "sec.offers.title": "Ofertas selecionadas do comércio local.",
        "sec.marketplace.eyebrow": "Mercado de Apps",
        "sec.marketplace.title": "Apps especializados criados por Desenvolvedores Certificados ADN.",
        "sec.partners.eyebrow": "Parceiros",
        "sec.partners.title": "Primeiros 100 negócios. Garantidos para sempre.",
        "sec.partners.biz": "Parceiros Comerciais",
        "sec.partners.biz.cta": "Torne-se um Parceiro Comercial",
        "sec.partners.dev": "Desenvolvedores de Apps",
        "sec.voucher.eyebrow": "Voucher de Hotel",
        "sec.voucher.title": "Seu voucher de hotel de $100, por nossa conta.",
        "sec.voucher.body": "Ative sua associação VIP Fundador e receba um código único de voucher de hotel de $100 em seu painel de membro. Resgate sujeito aos termos do parceiro, datas indisponíveis e estadias mínimas.",
        "sec.voucher.badge.eyebrow": "Recompensa VIP Fundador",
        "sec.voucher.badge.value": "Voucher de Hotel de $100",

        "faq.eyebrow": "Perguntas frequentes",
        "faq.title": "Tudo o que você quer saber.",

        "cta.title": "O preço VIP Fundador não vai durar para sempre.",

        "footer.tagline": "Rede de Recompensas e Fidelidade Local™. Celebrando comunidade, comércio e 250 anos do espírito americano.",
        "footer.col.platform": "Plataforma",
        "footer.col.legal": "Legal",
        "footer.col.compliance": "Conformidade",

        "counter.title": "Associações VIP Fundador",
        "counter.limit": "Limitado a 50.000",
        "counter.claimed": "reivindicadas",
        "counter.remaining": "associações disponíveis",
        "counter.lock": "Garanta $9.99/mês para sempre",

        "login.eyebrow": "Entrar",
        "login.title": "Bem-vindo de volta ao ADN",
        "login.email": "E-mail",
        "login.password": "Senha",
        "login.submit": "Entrar",
        "login.submitting": "Entrando…",
        "login.forgot": "Esqueceu a senha?",
        "login.no_account": "Não tem uma conta?",
        "login.create": "Crie uma",
        "forgot.title": "Esqueceu a senha?",
        "forgot.subtitle": "Digite o e-mail da sua conta ADN e enviaremos um link seguro para redefinir.",
        "forgot.email": "Endereço de e-mail",
        "forgot.submit": "Enviar link de redefinição",
        "forgot.submitting": "Enviando link…",
        "forgot.sent_title": "Verifique sua caixa de entrada",
        "forgot.sent_msg": "Se corresponder a uma conta ADN, o link está a caminho. Expira em 30 minutos.",
        "forgot.sent_spam": "Não recebeu? Veja o spam ou tente novamente em alguns minutos.",
        "forgot.back": "Voltar ao login",
        "reset.title": "Escolha uma nova senha",
        "reset.subtitle": "Seu link é válido por 30 minutos. Use pelo menos 8 caracteres.",
        "reset.password": "Nova senha",
        "reset.confirm": "Confirmar senha",
        "reset.submit": "Atualizar senha",
        "reset.submitting": "Atualizando…",
        "reset.success": "Senha atualizada. Entre com a nova.",
        "reset.err.missing": "Token ausente. Use o link do seu e-mail.",
        "reset.err.short": "Mínimo de 8 caracteres.",
        "reset.err.match": "As senhas não coincidem.",
        "reset.err.fail": "Falha. O link pode ter expirado.",

        "reg.title": "Junte-se Uma Vez. Economize Para Sempre.",
        "reg.step1": "1. Criar conta",
        "reg.step2": "2. Ativação",
        "reg.step3": "3. Boas-vindas",
        "reg.first": "Nome",
        "reg.last": "Sobrenome",
        "reg.email": "E-mail",
        "reg.password": "Senha (mínimo 8 caracteres)",
        "reg.have_account": "Já tem uma conta?",
        "reg.signin": "Entrar",

        "countdown.eyebrow": "Campanha VIP Fundador · Termina",
        "countdown.eyebrow_expired": "Campanha VIP Fundador · Encerrada",
        "countdown.tagline.before": "Garanta sua",
        "countdown.tagline.rate": "taxa Fundador de $9,99/mês",
        "countdown.tagline.after": "antes que o tempo acabe.",
        "countdown.tagline.expired": "Preço padrão agora em vigor",
        "countdown.label.days": "Dias",
        "countdown.label.hours": "Hrs",
        "countdown.label.mins": "Min",
        "countdown.label.secs": "Seg",
        "countdown.progress.launched": "Campanha iniciada",
        "countdown.progress.complete": "% concluído",
        "countdown.progress.ends": "Campanha termina",
        "countdown.expired_badge": "Campanha Encerrada",

        "july4.eyebrow": "Destaque · 4 de Julho de 2026",
        "july4.title": "Celebre 250 Anos Com Sua Comunidade",
        "july4.subtitle": "Vozes locais, famílias, empresas, igrejas, veteranos, jovens e parceiros comunitários celebrando a Celebração de 4 de Julho de 250 Anos.",
        "july4.cta.watch": "Ver Vídeos da Celebração",
        "july4.cta.submit": "Enviar Seu Vídeo",

        "install.title": "Instalar ADN Rewards",
        "install.body": "Tenha o app em tela cheia na sua tela inicial — acesso mais rápido a vouchers, ofertas e ao concierge de IA.",
        "install.body_ios_a": "Toque em",
        "install.body_ios_b": "Compartilhar",
        "install.body_ios_c": "e em",
        "install.body_ios_d": "Adicionar à Tela Inicial",
        "install.body_ios_e": "para a experiência completa.",
        "install.cta": "Instalar App",

        "common.loading": "Carregando…",
        "common.searching": "Buscando…",
        "common.signin_to": "Entre para",
        "common.clear": "Limpar",
        "common.all": "Todos",
        "common.cancel": "Cancelar",
        "common.save": "Salvar",
        "common.close": "Fechar",

        "cookies.title": "Usamos alguns cookies.",
        "cookies.body": "Cookies estritamente necessários mantêm você conectado. Os opcionais ajudam a melhorar o ADN. Veja nossa",
        "cookies.privacy_link": "Política de Privacidade",
        "cookies.accept_all": "Aceitar todos",
        "cookies.essential": "Apenas essenciais",

        "founding.heading": "Membros Founding VIP",
        "founding.limit": "Limitado a 50.000",
        "founding.claimed": "reivindicados",
        "founding.available": "associações disponíveis",
        "founding.lock_rate": "Garanta $9,99/mês vitalício",
        "founding.disclaimer": "O preço Founding VIP fica bloqueado apenas para o membro original e não é transferível.",

        "offers.eyebrow": "Economia para membros · Jacksonville, FL",
        "offers.title": "Ofertas Locais",
        "offers.subtitle": "Ofertas selecionadas de empresas de Jacksonville em todos os bairros — de Riverside às Praias.",
        "offers.search.placeholder": "Pesquise por nome, bairro, CEP, categoria…",
        "offers.search.hint": "A pesquisa não diferencia maiúsculas — tente \"riverside\", \"32250\", \"BBQ\" ou \"San Marco\".",
        "offers.filter.all_neighborhoods": "Todos os bairros",
        "offers.filter.locating": "Localizando…",
        "offers.filter.use_location": "Usar minha localização",
        "offers.filter.sorted_by_distance": "Ordenado por distância · Limpar",
        "offers.filter.show_map": "Mostrar mapa",
        "offers.filter.hide_map": "Ocultar mapa",
        "offers.filter.clear": "Limpar filtros",
        "offers.count.one": "oferta encontrada",
        "offers.count.many": "ofertas encontradas",
        "offers.empty.title": "Nenhuma oferta corresponde aos seus filtros",
        "offers.empty.body": "Tente outro bairro, um CEP ou limpe os filtros.",
        "offers.empty.cta": "Limpar filtros",
        "offers.card.distance_suffix": "milhas",
        "offers.card.250years": "250 Anos",
        "offers.card.redeem": "Resgatar",
        "offers.card.signin_to_redeem": "Entre como membro ativo para resgatar.",
        "offers.toast.location_set": "Localização definida — ordenando por distância.",
        "offers.toast.location_unsupported": "Seu navegador não suporta geolocalização.",
        "offers.toast.location_fail": "Não foi possível obter sua localização.",
        "offers.toast.offer_saved": "Oferta salva",

        "marketplace.eyebrow": "Desenvolvedores Certificados ADN",
        "marketplace.title": "Mercado de Apps",
        "marketplace.subtitle": "Apps verificados com foco local — de redes de babás à contabilidade e centros de igreja.",
        "marketplace.loading": "Carregando apps…",
        "marketplace.empty": "Ainda não há apps nesta categoria.",
        "marketplace.by": "por",
        "marketplace.install": "Instalar",
        "marketplace.subscribe": "Assinar",
        "marketplace.buy": "Comprar",
        "marketplace.signin_to_purchase": "Entre para comprar.",

        "footer.col.terms": "Termos",
        "footer.col.privacy": "Privacidade",
        "footer.col.voucher_terms": "Termos do Voucher de Hotel",
        "footer.col.video_terms": "Termos de Envio de Vídeo",
        "footer.col.referral_terms": "Termos do Programa de Indicação",
        "footer.copyright": "Todos os direitos reservados.",

        "settings.title": "Personalize.",
        "settings.eyebrow": "Configurações do App",
        "settings.subtitle": "Idioma, voz, acessibilidade, atualizações. Tudo é salvo automaticamente e segue você entre visitas.",
        "settings.section.language": "Idioma do App",
        "settings.section.language.sub": "Usado em todo o app e no AI Concierge.",
        "settings.section.updates": "Atualizações e Versão",
        "settings.section.concierge": "AI Concierge",
        "settings.section.concierge.sub": "Como o Ask ADN responde a você.",
        "settings.section.notif": "Notificações",
        "settings.section.notif.sub": "Como o ADN te mantém informado.",
        "settings.section.display": "Tela e Acessibilidade",
        "settings.section.display.sub": "Conforto e movimento.",
        "settings.section.install": "Instalar e App",
        "settings.section.install.sub": "Tenha o ADN na sua tela inicial.",
        "settings.row.autodetect": "Detectar idioma na primeira visita",
        "settings.row.autodetect.hint": "Quando ativo, o ADN usa o idioma preferido do seu dispositivo da primeira vez. Após escolher um acima, sua escolha sempre prevalece.",
        "settings.row.check_updates": "Verificar atualizações agora",
        "settings.row.check_updates.hint": "Limpa os arquivos em cache, busca a versão mais recente e recarrega o app.",
        "settings.row.check_updates.cta": "Verificar atualizações",
        "settings.row.check_updates.busy": "Atualizando…",
        "settings.row.autospeak": "Ler respostas em voz alta",
        "settings.row.autospeak.hint": "O Concierge fala cada resposta usando a voz do seu dispositivo.",
        "settings.row.tts_rate": "Velocidade de leitura",
        "settings.row.tts_rate.hint": "Quão rápido as respostas são lidas em voz alta.",
        "settings.row.simple_mode": "Modo simples",
        "settings.row.simple_mode.hint": "Respostas em linguagem clara — frases curtas, sem jargão.",
        "settings.row.email_transcripts": "Enviar transcrições do chat quando eu encerrar uma conversa",
        "settings.row.clear_chat": "Limpar histórico do chat neste dispositivo",
        "settings.row.clear_chat.hint": "Inicia uma nova conversa. Suas transcrições anteriores permanecem em sua conta.",
        "settings.row.clear_chat.cta": "Limpar chat",
        "settings.row.browser_notif": "Notificações do navegador",
        "settings.row.browser_notif.hint": "Receba um aviso discreto quando uma nova oferta local aparecer ou sua janela de voucher abrir. Requer sua permissão.",
        "settings.row.browser_notif.enabled": "Ativado",
        "settings.row.browser_notif.enable": "Ativar",
        "settings.row.reduce_motion": "Reduzir movimento",
        "settings.row.reduce_motion.hint": "Desativa fades, parallax e animações decorativas.",
        "settings.row.larger_text": "Texto maior",
        "settings.row.larger_text.hint": "Aumenta o tamanho da fonte em 12% em todo o app.",
        "settings.row.install_status": "Status de instalação",
        "settings.row.install_status.installed": "O ADN Rewards está instalado como app neste dispositivo.",
        "settings.row.install_status.browser": "Você está usando o ADN em uma aba do navegador. Instale para uma experiência mais rápida e em tela cheia.",
        "settings.row.install_status.badge_installed": "Instalado",
        "settings.row.install_status.badge_browser": "Navegador",
        "settings.row.reset_install": "Mostrar aviso de instalação novamente",
        "settings.row.reset_install.hint": "Se você dispensou o aviso antes, isto traz de volta na próxima visita.",
        "settings.row.reset_install.cta": "Restaurar aviso",
        "settings.tts.slow": "Lenta",
        "settings.tts.normal": "Normal",
        "settings.tts.fast": "Rápida",
        "settings.privacy_center": "Centro de Privacidade",
        "settings.terms_of_service": "Termos de Serviço",
        "settings.notif.disclaimer": "Nunca enviamos pushes de marketing. As notificações são reservadas para eventos da conta (disponibilidade de voucher, aprovação de indicação) e alertas de ofertas locais que você optar por receber.",
        "settings.toast.lang_updated": "Idioma atualizado",
        "settings.toast.updates_checking": "Verificando atualizações…",
        "settings.toast.updates_checking.sub": "O app será recarregado em instantes.",
        "settings.toast.chat_cleared": "Histórico de chat limpo neste dispositivo",
        "settings.toast.install_reset": "O aviso de instalação aparecerá novamente na próxima visita",
        "settings.toast.notif_enabled": "Notificações ativadas",
        "settings.toast.notif_not_enabled": "As notificações não foram ativadas",
        "settings.toast.notif_not_enabled.sub": "Você pode alterar isso nas configurações do navegador.",
        "settings.toast.notif_unsupported": "Notificações não são suportadas neste dispositivo",
        "settings.confirm.clear_chat": "Encerrar a conversa atual e limpar o histórico do chat neste dispositivo?",

        "member.eyebrow": "Painel do Membro",
        "member.welcome": "Bem-vindo,",
        "member.member_id": "ID do Membro:",
        "member.badge.founding": "Founding VIP",
        "member.badge.lifetime": "Vitalícia $9,99/mês",
        "member.badge.active": "Ativa",
        "member.badge.inactive": "Inativa",
        "member.cta.activate": "Ativar Associação ($25)",
        "member.cta.settings": "Configurações",

        "faq.q1": "Isto é um investimento?",
        "faq.a1": "Não. ADN Local Rewards & Loyalty Network™ é uma plataforma paga de associação e recompensas. Não há propriedade, participação nos lucros, valores mobiliários, capital ou renda passiva.",
        "faq.q2": "O que cobrem os $25?",
        "faq.a2": "A ativação única Founding VIP de $25 reserva sua taxa vitalícia de $9,99/mês, inicia seus benefícios e emite seu voucher de hotel de $100.",
        "faq.q3": "Posso transferir minha associação?",
        "faq.a3": "Não. O preço Founding VIP fica bloqueado apenas para o membro original e não é transferível.",
        "faq.q4": "Por quanto tempo o preço Founding VIP está disponível?",
        "faq.a4": "O preço Founding VIP é reservado para os primeiros 50.000 membros. Atingido esse limite, aplica-se o preço padrão. Veja o contador ao vivo nesta página.",
        "faq.q5": "O que acontece se eu cancelar?",
        "faq.a5": "Você pode cancelar a qualquer momento no painel de membro e manterá acesso total por um período de carência de 7 dias. Reative dentro desses 7 dias e nada muda. Após o período de carência, sua associação e quaisquer funções de negócio, desenvolvedor ou embaixador ligadas a ela são pausadas.",
        "faq.q6": "E se meu pagamento falhar?",
        "faq.a6": "Não suspendemos imediatamente. Você entra em uma janela de carência para atualizar o cartão e continuar sem interrupção. Após esse período, a Core ADN Rule pausa seu perfil de negócio, listagens de apps e recompensas de embaixador até a restauração da associação.",
        "faq.q7": "Como funciona o voucher de hotel?",
        "faq.a7": "Depois da ativação, você receberá um código de voucher único de $100 no seu painel. Resgate com parceiros aprovados conforme os termos do voucher.",
        "faq.q8": "Quem pode enviar um vídeo de celebração?",
        "faq.a8": "Membros ativos podem enviar um vídeo para revisão. Todos os vídeos passam por aprovação, devem respeitar direitos autorais e seguir os termos de vídeo da comunidade.",
        "faq.q9": "O que é a oferta Founding Business Partner?",
        "faq.a9": "As primeiras 100 empresas locais a ingressar no ADN recebem o status Founding Business Partner™ — $0/mês para sempre (normalmente $49/mês), posicionamento prioritário no mapa e uma oferta ativa bloqueada vitaliciamente enquanto o proprietário mantiver uma associação ADN ativa.",
        "faq.q10": "Por que donos de empresas e desenvolvedores precisam de associação?",
        "faq.a10": "ADN é uma comunidade voltada para membros. A Core ADN Rule exige que cada dono de empresa, desenvolvedor certificado e embaixador mantenha uma associação ADN ativa.",
        "faq.q11": "Quais apps estão no marketplace?",
        "faq.a11": "Apps verificados com foco local de desenvolvedores ADN Certified — serviços, ferramentas, IA, educação, imóveis, apps de igreja e mais.",
        "faq.q12": "Como me torno um ADN Certified Developer?",
        "faq.a12": "Qualquer pessoa pode se inscrever. Faça o currículo de 12 semanas ADN AI App Certification. Apenas 100 certificações disponíveis ao preço de lançamento de $999 (regular $4.999). Bolsas disponíveis pelo portal de Parceiros. Associação ADN ativa é necessária durante todo o processo.",
        "faq.q13": "Como funcionam as indicações e o programa de Embaixadores?",
        "faq.a13": "Cada membro ativo recebe um link único. Você ganha recompensas por cada amigo que ativa, até 12 meses de créditos, e com 25 indicações bem-sucedidas sua taxa de $9,99/mês passa a ser vitalícia grátis.",
        "faq.q14": "Posso encontrar ofertas perto de mim?",
        "faq.a14": "Sim. Em Ofertas Locais, toque em \"Usar minha localização\" para ordenar empresas por distância. Você também pode filtrar por bairro, categoria ou buscar pelo nome ou CEP.",
        "faq.q15": "Onde o ADN opera?",
        "faq.a15": "Estamos lançando em Jacksonville, FL junto com a celebração de 4 de julho de 2026 do America 250. Novas cidades fundadoras serão adicionadas conforme a demanda dos membros.",
        "faq.q16": "Minhas informações estão seguras?",
        "faq.a16": "Sim. O ADN usa autenticação criptografada, nunca vende seus dados pessoais e compartilha apenas o mínimo necessário para resgatar uma oferta. Detalhes na política de Privacidade.",
        "faq.q17": "Como obtenho ajuda?",
        "faq.a17": "Entre em contato com o suporte pelo painel de membro ou pelo link de contato no rodapé. Membros Founding VIP recebem resposta prioritária.",
        "faq.q18": "O que é a ADN?",
        "faq.a18": "ADN — sigla de Axxess Denied Network — é um marketplace de impacto comunitário em Jacksonville. Ajudamos negócios locais a crescer conectando-os diretamente a membros que querem apoiar seu bairro, e damos visibilidade às campanhas comunitárias.",
        "faq.q19": "Como ganho créditos ADN?",
        "faq.a19": "Os membros ganham créditos ADN cada vez que resgatam uma oferta local, indicam um amigo ou contribuem para uma campanha comunitária. Os créditos desbloqueiam descontos extras, vouchers de hotel e benefícios exclusivos.",
        "faq.q20": "Como posso apoiar um negócio local?",
        "faq.a20": "Torne-se membro, resgate ofertas, compartilhe o perfil com vizinhos e deixe avaliações honestas. Cada resgate envia receita real diretamente ao negócio de Jacksonville — sem intermediários.",
    },

    fr: {
        "nav.videos": "Vidéos des 250 Ans",
        "nav.offers": "Offres Locales",
        "nav.marketplace": "Marché d'Applis",
        "nav.partners": "Partenaires",
        "nav.signin": "Connexion",
        "nav.join": "Devenez VIP Fondateur",
        "nav.dashboard": "Tableau de bord",
        "nav.developer": "Développeur",
        "nav.admin": "Administrateur",
        "nav.signout": "Déconnexion",

        "hero.eyebrow": "4 Juillet 2026 · Célébration des 250 Ans™",
        "hero.title.line1": "Adhérez Une Fois.",
        "hero.title.line2a": "Économisez À Vie.",
        "hero.title.line2b": "Découvrez Plus.",
        "hero.subhead.before": "Devenez un",
        "hero.subhead.role": "Membre VIP Fondateur",
        "hero.subhead.middle": "pour une activation unique de 25$ et verrouillez",
        "hero.subhead.price": "9,99$/mois à vie",
        "hero.subhead.tail": "Recevez un bon d'hôtel de 100$, des économies locales, une vitrine vidéo communautaire et l'accès à un marché d'applications locales vérifiées.",
        "hero.cta.join": "Devenez VIP Fondateur",
        "hero.cta.videos": "Regarder les Vidéos de la Célébration",
        "hero.badge.secure": "Adhésion sécurisée",
        "hero.badge.voucher": "Bon d'hôtel de 100$",
        "hero.badge.rate": "Tarif à vie",
        "hero.counter.footnote": "Le tarif VIP Fondateur est verrouillé pour le membre original uniquement et n'est pas transférable.",

        "sec.benefits.title": "Une adhésion communautaire conçue pour prendre de la valeur.",
        "sec.how.eyebrow": "Comment ça marche",
        "sec.how.title": "Trois étapes vers une vie locale.",
        "sec.offers.eyebrow": "Offres Locales",
        "sec.offers.title": "Offres triées sur le volet de la rue principale.",
        "sec.marketplace.eyebrow": "Marché d'Applis",
        "sec.marketplace.title": "Applications de niche conçues par les Développeurs Certifiés ADN.",
        "sec.partners.eyebrow": "Partenaires",
        "sec.partners.title": "Les 100 premières entreprises. Verrouillées à vie.",
        "sec.partners.biz": "Partenaires Commerciaux",
        "sec.partners.biz.cta": "Devenir un Partenaire Commercial",
        "sec.partners.dev": "Développeurs d'Applis",
        "sec.voucher.eyebrow": "Bon d'Hôtel",
        "sec.voucher.title": "Votre bon d'hôtel de 100$, offert.",
        "sec.voucher.body": "Activez votre adhésion VIP Fondateur et recevez un code unique de bon d'hôtel de 100$ dans votre tableau de bord. Échange soumis aux conditions du partenaire, dates de fermeture et séjours minimums.",
        "sec.voucher.badge.eyebrow": "Récompense VIP Fondateur",
        "sec.voucher.badge.value": "Bon d'Hôtel de 100$",

        "faq.eyebrow": "Questions fréquentes",
        "faq.title": "Tout ce que vous voulez savoir.",

        "cta.title": "Le tarif VIP Fondateur ne durera pas éternellement.",

        "footer.tagline": "Réseau de Récompenses et de Fidélité Locale™. Célébrant la communauté, le commerce et 250 ans d'esprit américain.",
        "footer.col.platform": "Plateforme",
        "footer.col.legal": "Légal",
        "footer.col.compliance": "Conformité",

        "counter.title": "Adhésions VIP Fondateur",
        "counter.limit": "Limité à 50 000",
        "counter.claimed": "réclamées",
        "counter.remaining": "adhésions restantes",
        "counter.lock": "Verrouillez 9,99$/mois à vie",

        "login.eyebrow": "Connexion",
        "login.title": "Bon retour sur ADN",
        "login.email": "Courriel",
        "login.password": "Mot de passe",
        "login.submit": "Se connecter",
        "login.submitting": "Connexion…",
        "login.forgot": "Mot de passe oublié?",
        "login.no_account": "Vous n'avez pas de compte?",
        "login.create": "Créez-en un",
        "forgot.title": "Mot de passe oublié ?",
        "forgot.subtitle": "Saisissez l'e-mail lié à votre compte ADN et nous vous enverrons un lien de réinitialisation sécurisé.",
        "forgot.email": "Adresse e-mail",
        "forgot.submit": "Envoyez-moi le lien",
        "forgot.submitting": "Envoi du lien…",
        "forgot.sent_title": "Vérifiez votre boîte de réception",
        "forgot.sent_msg": "Si cela correspond à un compte ADN, le lien est en route. Il expire dans 30 minutes.",
        "forgot.sent_spam": "Pas reçu ? Vérifiez le spam ou réessayez dans quelques minutes.",
        "forgot.back": "Retour à la connexion",
        "reset.title": "Choisir un nouveau mot de passe",
        "reset.subtitle": "Votre lien est valide 30 minutes. Au moins 8 caractères.",
        "reset.password": "Nouveau mot de passe",
        "reset.confirm": "Confirmer le mot de passe",
        "reset.submit": "Mettre à jour",
        "reset.submitting": "Mise à jour…",
        "reset.success": "Mot de passe mis à jour. Connectez-vous avec le nouveau.",
        "reset.err.missing": "Jeton manquant. Utilisez le lien de votre e-mail.",
        "reset.err.short": "Au moins 8 caractères.",
        "reset.err.match": "Les mots de passe ne correspondent pas.",
        "reset.err.fail": "Échec. Le lien a peut-être expiré.",

        "reg.title": "Adhérez Une Fois. Économisez À Vie.",
        "reg.step1": "1. Créer un compte",
        "reg.step2": "2. Activation",
        "reg.step3": "3. Bienvenue",
        "reg.first": "Prénom",
        "reg.last": "Nom",
        "reg.email": "Courriel",
        "reg.password": "Mot de passe (8 caractères min)",
        "reg.have_account": "Vous avez déjà un compte?",
        "reg.signin": "Se connecter",

        "countdown.eyebrow": "Drive VIP Fondateur · Se termine",
        "countdown.eyebrow_expired": "Drive VIP Fondateur · Clôturé",
        "countdown.tagline.before": "Verrouillez votre",
        "countdown.tagline.rate": "tarif Fondateur de 9,99 $/mois",
        "countdown.tagline.after": "avant la fin du décompte.",
        "countdown.tagline.expired": "Tarif standard maintenant en vigueur",
        "countdown.label.days": "Jours",
        "countdown.label.hours": "Hrs",
        "countdown.label.mins": "Min",
        "countdown.label.secs": "Sec",
        "countdown.progress.launched": "Drive lancé",
        "countdown.progress.complete": "% complété",
        "countdown.progress.ends": "Drive se termine",
        "countdown.expired_badge": "Drive Clôturé",

        "july4.eyebrow": "À l'affiche · 4 Juillet 2026",
        "july4.title": "Célébrez 250 Ans Avec Votre Communauté",
        "july4.subtitle": "Voix locales, familles, entreprises, églises, vétérans, jeunes et partenaires communautaires célébrant la Célébration du 4 Juillet des 250 Ans.",
        "july4.cta.watch": "Voir les Vidéos de la Célébration",
        "july4.cta.submit": "Soumettre Votre Vidéo",

        "install.title": "Installer ADN Rewards",
        "install.body": "Obtenez l'app en plein écran sur votre écran d'accueil — accès plus rapide aux bons, offres et au concierge IA.",
        "install.body_ios_a": "Touchez",
        "install.body_ios_b": "Partager",
        "install.body_ios_c": "puis",
        "install.body_ios_d": "Ajouter à l'écran d'accueil",
        "install.body_ios_e": "pour l'expérience complète.",
        "install.cta": "Installer l'app",

        "common.loading": "Chargement…",
        "common.searching": "Recherche…",
        "common.signin_to": "Connectez-vous pour",
        "common.clear": "Effacer",
        "common.all": "Tous",
        "common.cancel": "Annuler",
        "common.save": "Enregistrer",
        "common.close": "Fermer",

        "cookies.title": "Nous utilisons quelques cookies.",
        "cookies.body": "Les cookies strictement nécessaires vous maintiennent connecté. Les optionnels nous aident à améliorer ADN. Voir notre",
        "cookies.privacy_link": "Politique de Confidentialité",
        "cookies.accept_all": "Tout accepter",
        "cookies.essential": "Essentiels seulement",

        "founding.heading": "Adhésions Founding VIP",
        "founding.limit": "Limité à 50 000",
        "founding.claimed": "réclamées",
        "founding.available": "adhésions disponibles",
        "founding.lock_rate": "Verrouillez 9,99 $/mois à vie",
        "founding.disclaimer": "Le tarif Founding VIP est verrouillé uniquement pour le membre d'origine et n'est pas transférable.",

        "offers.eyebrow": "Économies membres · Jacksonville, FL",
        "offers.title": "Offres Locales",
        "offers.subtitle": "Offres triées sur le volet de commerces de Jacksonville dans chaque quartier — de Riverside aux Plages.",
        "offers.search.placeholder": "Rechercher par nom, quartier, code postal, catégorie…",
        "offers.search.hint": "La recherche ignore la casse — essayez \"riverside\", \"32250\", \"BBQ\" ou \"San Marco\".",
        "offers.filter.all_neighborhoods": "Tous les quartiers",
        "offers.filter.locating": "Localisation…",
        "offers.filter.use_location": "Utiliser ma position",
        "offers.filter.sorted_by_distance": "Trié par distance · Effacer",
        "offers.filter.show_map": "Afficher la carte",
        "offers.filter.hide_map": "Masquer la carte",
        "offers.filter.clear": "Effacer les filtres",
        "offers.count.one": "offre trouvée",
        "offers.count.many": "offres trouvées",
        "offers.empty.title": "Aucune offre ne correspond à vos filtres",
        "offers.empty.body": "Essayez un autre quartier, un code postal, ou effacez les filtres.",
        "offers.empty.cta": "Effacer les filtres",
        "offers.card.distance_suffix": "mi",
        "offers.card.250years": "250 Ans",
        "offers.card.redeem": "Échanger",
        "offers.card.signin_to_redeem": "Connectez-vous en tant que membre actif pour échanger.",
        "offers.toast.location_set": "Position définie — tri par distance.",
        "offers.toast.location_unsupported": "Votre navigateur ne supporte pas la géolocalisation.",
        "offers.toast.location_fail": "Impossible d'obtenir votre position.",
        "offers.toast.offer_saved": "Offre enregistrée",

        "marketplace.eyebrow": "Développeurs Certifiés ADN",
        "marketplace.title": "Marketplace d'Apps",
        "marketplace.subtitle": "Apps vérifiées à vocation locale — du réseau de baby-sitters à la comptabilité en passant par les hubs d'église.",
        "marketplace.loading": "Chargement des apps…",
        "marketplace.empty": "Aucune app dans cette catégorie pour l'instant.",
        "marketplace.by": "par",
        "marketplace.install": "Installer",
        "marketplace.subscribe": "S'abonner",
        "marketplace.buy": "Acheter",
        "marketplace.signin_to_purchase": "Connectez-vous pour acheter.",

        "footer.col.terms": "Conditions",
        "footer.col.privacy": "Confidentialité",
        "footer.col.voucher_terms": "Conditions du Bon d'Hôtel",
        "footer.col.video_terms": "Conditions de Soumission Vidéo",
        "footer.col.referral_terms": "Conditions du Programme de Parrainage",
        "footer.copyright": "Tous droits réservés.",

        "settings.title": "Personnalisez.",
        "settings.eyebrow": "Paramètres de l'App",
        "settings.subtitle": "Langue, voix, accessibilité, mises à jour. Tout s'enregistre automatiquement et vous suit entre les visites.",
        "settings.section.language": "Langue de l'App",
        "settings.section.language.sub": "Utilisée dans toute l'app et le AI Concierge.",
        "settings.section.updates": "Mises à jour et Version",
        "settings.section.concierge": "AI Concierge",
        "settings.section.concierge.sub": "Comment Ask ADN vous répond.",
        "settings.section.notif": "Notifications",
        "settings.section.notif.sub": "Comment ADN vous tient informé.",
        "settings.section.display": "Affichage et Accessibilité",
        "settings.section.display.sub": "Confort et mouvement.",
        "settings.section.install": "Installer et App",
        "settings.section.install.sub": "Mettez ADN sur votre écran d'accueil.",
        "settings.row.autodetect": "Détecter la langue à la première visite",
        "settings.row.autodetect.hint": "Quand activé, ADN utilise la langue préférée de votre appareil la toute première fois. Après en avoir choisi une ci-dessus, votre choix prévaut toujours.",
        "settings.row.check_updates": "Vérifier les mises à jour",
        "settings.row.check_updates.hint": "Efface les fichiers en cache, récupère la dernière version et recharge l'app.",
        "settings.row.check_updates.cta": "Vérifier les mises à jour",
        "settings.row.check_updates.busy": "Mise à jour…",
        "settings.row.autospeak": "Lire les réponses à voix haute",
        "settings.row.autospeak.hint": "Le Concierge prononce chaque réponse avec la voix de votre appareil.",
        "settings.row.tts_rate": "Vitesse de lecture",
        "settings.row.tts_rate.hint": "À quelle vitesse les réponses sont lues à voix haute.",
        "settings.row.simple_mode": "Mode simple",
        "settings.row.simple_mode.hint": "Réponses en langage clair — phrases courtes, sans jargon.",
        "settings.row.email_transcripts": "M'envoyer les transcriptions du chat à la fin d'une conversation",
        "settings.row.clear_chat": "Effacer l'historique du chat sur cet appareil",
        "settings.row.clear_chat.hint": "Démarre une nouvelle conversation. Vos transcriptions passées restent dans votre compte.",
        "settings.row.clear_chat.cta": "Effacer le chat",
        "settings.row.browser_notif": "Notifications du navigateur",
        "settings.row.browser_notif.hint": "Recevez une alerte discrète lorsqu'une nouvelle offre locale arrive ou que votre fenêtre de bon s'ouvre. Nécessite votre permission.",
        "settings.row.browser_notif.enabled": "Activées",
        "settings.row.browser_notif.enable": "Activer",
        "settings.row.reduce_motion": "Réduire les mouvements",
        "settings.row.reduce_motion.hint": "Désactive les fondus, la parallaxe et les animations décoratives.",
        "settings.row.larger_text": "Texte plus grand",
        "settings.row.larger_text.hint": "Augmente la taille de base de la police de 12 % dans toute l'app.",
        "settings.row.install_status": "Statut d'installation",
        "settings.row.install_status.installed": "ADN Rewards est installé comme app sur cet appareil.",
        "settings.row.install_status.browser": "Vous utilisez ADN dans un onglet de navigateur. Installez pour une expérience plein écran plus rapide.",
        "settings.row.install_status.badge_installed": "Installé",
        "settings.row.install_status.badge_browser": "Navigateur",
        "settings.row.reset_install": "Réafficher l'invite d'installation",
        "settings.row.reset_install.hint": "Si vous avez ignoré l'invite plus tôt, cela la fait revenir à la prochaine visite.",
        "settings.row.reset_install.cta": "Réinitialiser l'invite",
        "settings.tts.slow": "Lente",
        "settings.tts.normal": "Normale",
        "settings.tts.fast": "Rapide",
        "settings.privacy_center": "Centre de Confidentialité",
        "settings.terms_of_service": "Conditions de Service",
        "settings.notif.disclaimer": "Nous n'envoyons jamais de pushes marketing. Les notifications sont réservées aux événements de compte (disponibilité de bon, approbation de parrainage) et aux alertes d'offres locales auxquelles vous vous abonnez.",
        "settings.toast.lang_updated": "Langue mise à jour",
        "settings.toast.updates_checking": "Recherche de mises à jour…",
        "settings.toast.updates_checking.sub": "L'app va se recharger dans un instant.",
        "settings.toast.chat_cleared": "Historique du chat effacé sur cet appareil",
        "settings.toast.install_reset": "L'invite d'installation réapparaîtra à votre prochaine visite",
        "settings.toast.notif_enabled": "Notifications activées",
        "settings.toast.notif_not_enabled": "Les notifications n'ont pas été activées",
        "settings.toast.notif_not_enabled.sub": "Vous pouvez modifier cela dans les paramètres de votre navigateur.",
        "settings.toast.notif_unsupported": "Les notifications ne sont pas prises en charge sur cet appareil",
        "settings.confirm.clear_chat": "Terminer la conversation en cours et effacer l'historique du chat sur cet appareil ?",

        "member.eyebrow": "Tableau de bord du Membre",
        "member.welcome": "Bienvenue,",
        "member.member_id": "ID du Membre :",
        "member.badge.founding": "Founding VIP",
        "member.badge.lifetime": "À vie 9,99 $/mois",
        "member.badge.active": "Active",
        "member.badge.inactive": "Inactive",
        "member.cta.activate": "Activer l'adhésion (25 $)",
        "member.cta.settings": "Paramètres",

        "faq.q1": "Est-ce un investissement ?",
        "faq.a1": "Non. ADN Local Rewards & Loyalty Network™ est une plateforme payante d'adhésion et de récompenses. Aucune propriété, partage de bénéfices, valeurs mobilières, capital ou revenu passif.",
        "faq.q2": "Que couvrent les 25 $ ?",
        "faq.a2": "L'activation unique Founding VIP de 25 $ verrouille votre tarif à vie de 9,99 $/mois, lance vos avantages et émet votre bon d'hôtel de 100 $.",
        "faq.q3": "Puis-je transférer mon adhésion ?",
        "faq.a3": "Non. Le tarif Founding VIP est verrouillé uniquement pour le membre d'origine et n'est pas transférable.",
        "faq.q4": "Combien de temps le tarif Founding VIP est-il disponible ?",
        "faq.a4": "Le tarif Founding VIP est réservé aux 50 000 premiers membres. Une fois ce plafond atteint, le tarif standard s'applique. Voyez le compteur en direct sur cette page.",
        "faq.q5": "Que se passe-t-il si j'annule ?",
        "faq.a5": "Vous pouvez annuler à tout moment depuis votre tableau de bord et conserver l'accès complet pendant une période de grâce de 7 jours. Après cette période, votre adhésion et tous les rôles d'entreprise, développeur ou ambassadeur associés sont suspendus.",
        "faq.q6": "Et si mon paiement échoue ?",
        "faq.a6": "Nous ne vous suspendons pas immédiatement. Vous entrez dans une fenêtre de grâce pour mettre à jour votre carte. Après ce délai, la Core ADN Rule suspend votre profil d'entreprise, vos apps et vos récompenses d'ambassadeur.",
        "faq.q7": "Comment fonctionne le bon d'hôtel ?",
        "faq.a7": "Une fois activé, vous recevrez un code de bon unique de 100 $ dans votre tableau de bord. Échangeable avec les partenaires approuvés selon les conditions du bon.",
        "faq.q8": "Qui peut soumettre une vidéo de célébration ?",
        "faq.a8": "Les membres actifs peuvent soumettre une vidéo pour examen. Toutes les vidéos sont approuvées et doivent respecter les droits d'auteur et les conditions vidéo de la communauté.",
        "faq.q9": "Qu'est-ce que l'offre Founding Business Partner ?",
        "faq.a9": "Les 100 premières entreprises locales à rejoindre ADN obtiennent le statut Founding Business Partner™ — 0 $/mois à vie (normalement 49 $/mois), placement prioritaire sur la carte et une offre active verrouillée à vie tant que le propriétaire conserve une adhésion ADN active.",
        "faq.q10": "Pourquoi les propriétaires et développeurs ont-ils besoin d'une adhésion ?",
        "faq.a10": "ADN est une communauté axée sur les membres. La Core ADN Rule exige que chaque propriétaire d'entreprise, développeur certifié et ambassadeur maintienne une adhésion ADN active.",
        "faq.q11": "Quelles apps sont dans le marketplace ?",
        "faq.a11": "Apps vérifiées à vocation locale par les développeurs ADN Certified — services, outils, IA, éducation, immobilier, apps d'église et plus.",
        "faq.q12": "Comment devenir un ADN Certified Developer ?",
        "faq.a12": "Tout le monde peut postuler. Inscrivez-vous au cursus ADN AI App Certification de 12 semaines. Seulement 100 certifications disponibles au prix de lancement de 999 $ (prix régulier 4 999 $). Bourses disponibles via le portail Partenaires. Adhésion ADN active requise tout au long.",
        "faq.q13": "Comment fonctionnent les parrainages et le programme Ambassadeur ?",
        "faq.a13": "Chaque membre actif reçoit un lien de parrainage unique. Vous gagnez des récompenses pour chaque ami qui s'active, jusqu'à 12 mois de crédits, et avec 25 parrainages réussis votre tarif de 9,99 $/mois devient gratuit à vie.",
        "faq.q14": "Puis-je trouver des offres près de moi ?",
        "faq.a14": "Oui. Sur la page Offres Locales, touchez \"Utiliser ma position\" pour trier par distance. Vous pouvez aussi filtrer par quartier, catégorie ou rechercher par nom ou code postal.",
        "faq.q15": "Où ADN opère-t-il ?",
        "faq.a15": "Nous lançons à Jacksonville, FL avec la célébration America 250 du 4 juillet 2026. De nouvelles villes fondatrices seront ajoutées selon la demande des membres.",
        "faq.q16": "Mes informations sont-elles sécurisées ?",
        "faq.a16": "Oui. ADN utilise l'authentification chiffrée, ne vend jamais vos données personnelles, et ne partage que le minimum requis. Détails dans la politique de Confidentialité.",
        "faq.q17": "Comment obtenir de l'aide ?",
        "faq.a17": "Contactez le support depuis votre tableau de bord ou via le lien de contact en pied de page. Les membres Founding VIP reçoivent une réponse prioritaire.",
        "faq.q18": "Qu'est-ce qu'ADN ?",
        "faq.a18": "ADN — pour Axxess Denied Network — est une place de marché d'impact communautaire à Jacksonville. Nous aidons les commerces locaux à se développer en les connectant directement aux membres qui veulent soutenir leur quartier.",
        "faq.q19": "Comment gagner des crédits ADN ?",
        "faq.a19": "Les membres gagnent des crédits ADN à chaque fois qu'ils utilisent une offre locale, parrainent un ami ou contribuent à une campagne communautaire. Les crédits débloquent des réductions, des bons d'hôtel et des avantages exclusifs.",
        "faq.q20": "Comment soutenir un commerce local ?",
        "faq.a20": "Devenez membre, utilisez ses offres, partagez son profil avec vos voisins et laissez des avis honnêtes. Chaque utilisation envoie des revenus réels directement au commerce de Jacksonville — sans intermédiaire.",
    },

    ht: {
        "nav.videos": "Videyo 250 Ane yo",
        "nav.offers": "Òf Lokal",
        "nav.marketplace": "Mache Aplikasyon",
        "nav.partners": "Patnè",
        "nav.signin": "Konekte",
        "nav.join": "Vin yon VIP Fondatè",
        "nav.dashboard": "Tablodbò",
        "nav.developer": "Devlopè",
        "nav.admin": "Administratè",
        "nav.signout": "Dekonekte",

        "hero.eyebrow": "4 Jiyè 2026 · Selebrasyon 250 Ane yo™",
        "hero.title.line1": "Antre Yon Fwa.",
        "hero.title.line2a": "Ekonomize Pou Tout Lavi.",
        "hero.title.line2b": "Dekouvri Plis.",
        "hero.subhead.before": "Vin yon",
        "hero.subhead.role": "Manm VIP Fondatè",
        "hero.subhead.middle": "pou yon aktivasyon $25 inisyal epi sere",
        "hero.subhead.price": "$9.99/mwa pou tout lavi",
        "hero.subhead.tail": "Resevwa yon koupon otèl $100, ekonomi lokal, yon vitrin videyo kominote, ak aksè nan yon mache aplikasyon lokal verifye.",
        "hero.cta.join": "Vin yon VIP Fondatè",
        "hero.cta.videos": "Gade Videyo Selebrasyon yo",
        "hero.badge.secure": "Manm sekirize",
        "hero.badge.voucher": "Koupon otèl $100",
        "hero.badge.rate": "To pou tout lavi",
        "hero.counter.footnote": "Pri VIP Fondatè a sere sèlman pou manm orijinal la epi li pa transferab.",

        "sec.benefits.title": "Yon manm kominote ki fèt pou ogmante valè li.",
        "sec.how.eyebrow": "Kòman li fonksyone",
        "sec.how.title": "Twa etap pou yon lavi lokal.",
        "sec.offers.eyebrow": "Òf Lokal",
        "sec.offers.title": "Òf chwazi nan komèsan lokal yo.",
        "sec.marketplace.eyebrow": "Mache Aplikasyon",
        "sec.marketplace.title": "Aplikasyon espesyalize Devlopè Sètifye ADN fè.",
        "sec.partners.eyebrow": "Patnè",
        "sec.partners.title": "Premye 100 antrepriz yo. Sere pou tout lavi.",
        "sec.partners.biz": "Patnè Komèsyal",
        "sec.partners.biz.cta": "Vin yon Patnè Komèsyal",
        "sec.partners.dev": "Devlopè Aplikasyon",
        "sec.voucher.eyebrow": "Koupon Otèl",
        "sec.voucher.title": "Koupon otèl $100 ou a, sou nou.",
        "sec.voucher.body": "Aktive manm VIP Fondatè ou epi resevwa yon kòd inik koupon otèl $100 nan tablodbò manm ou. Redanmsyon depann de kondisyon patnè, dat fèmen ak sejou minimòm.",
        "sec.voucher.badge.eyebrow": "Rekonpans VIP Fondatè",
        "sec.voucher.badge.value": "Koupon Otèl $100",

        "faq.eyebrow": "Kesyon yo poze souvan",
        "faq.title": "Tout sa ou ta vle konnen.",

        "cta.title": "Pri VIP Fondatè a p ap dire pou tout tan.",

        "footer.tagline": "Rezo Rekonpans ak Fidelite Lokal™. Selebre kominote, komès, ak 250 ane lespri ameriken.",
        "footer.col.platform": "Platfòm",
        "footer.col.legal": "Legal",
        "footer.col.compliance": "Konfòmite",

        "counter.title": "Manm VIP Fondatè",
        "counter.limit": "Limite a 50,000",
        "counter.claimed": "deja pran",
        "counter.remaining": "manm ki rete",
        "counter.lock": "Sere $9.99/mwa pou tout lavi",

        "login.eyebrow": "Konekte",
        "login.title": "Bon retou sou ADN",
        "login.email": "Imel",
        "login.password": "Modpas",
        "login.submit": "Konekte",
        "login.submitting": "K ap konekte…",
        "login.forgot": "Bliye modpas?",
        "login.no_account": "Ou pa gen kont?",
        "login.create": "Kreye youn",
        "forgot.title": "Bliye modpas ou?",
        "forgot.subtitle": "Antre imel ki konekte ak kont ADN ou epi n ap voye yon lyen sekirite pou reyinisyalize l.",
        "forgot.email": "Adrès imel",
        "forgot.submit": "Voye yon lyen reyinisyalize",
        "forgot.submitting": "N ap voye lyen an…",
        "forgot.sent_title": "Tcheke bwat resepsyon ou",
        "forgot.sent_msg": "Si li koresponn ak yon kont ADN, lyen an pral rive. Li ekspire nan 30 minit.",
        "forgot.sent_spam": "Pa resevwa l? Gade nan spam, oswa eseye ankò nan kèk minit.",
        "forgot.back": "Retounen nan koneksyon",
        "reset.title": "Chwazi yon nouvo modpas",
        "reset.subtitle": "Lyen ou valab pandan 30 minit. Omwen 8 karaktè.",
        "reset.password": "Nouvo modpas",
        "reset.confirm": "Konfime modpas",
        "reset.submit": "Mete modpas ajou",
        "reset.submitting": "N ap mete ajou…",
        "reset.success": "Modpas mete ajou. Konekte ak nouvo a.",
        "reset.err.missing": "Pa gen jeton. Sèvi ak lyen ki nan imel ou.",
        "reset.err.short": "Omwen 8 karaktè.",
        "reset.err.match": "Modpas yo pa matche.",
        "reset.err.fail": "Echwe. Lyen an ka ekspire.",

        "reg.title": "Antre Yon Fwa. Ekonomize Pou Tout Lavi.",
        "reg.step1": "1. Kreye kont",
        "reg.step2": "2. Aktivasyon",
        "reg.step3": "3. Byenveni",
        "reg.first": "Premye non",
        "reg.last": "Siyati",
        "reg.email": "Imel",
        "reg.password": "Modpas (omwen 8 karaktè)",
        "reg.have_account": "Ou gen yon kont deja?",
        "reg.signin": "Konekte",

        "countdown.eyebrow": "Drive VIP Fondatè · Fini",
        "countdown.eyebrow_expired": "Drive VIP Fondatè · Fèmen",
        "countdown.tagline.before": "Bloke",
        "countdown.tagline.rate": "to Fondatè $9.99/mwa",
        "countdown.tagline.after": "anvan tan an fini.",
        "countdown.tagline.expired": "Pri estanda kounye a an aplikasyon",
        "countdown.label.days": "Jou",
        "countdown.label.hours": "Èdt",
        "countdown.label.mins": "Min",
        "countdown.label.secs": "Sek",
        "countdown.progress.launched": "Drive lanse",
        "countdown.progress.complete": "% konplete",
        "countdown.progress.ends": "Drive fini",
        "countdown.expired_badge": "Drive Fèmen",

        "july4.eyebrow": "Espesyal · 4 Jiyè 2026",
        "july4.title": "Selebre 250 Ane Avèk Kominote w",
        "july4.subtitle": "Vwa lokal, fanmi, biznis, legliz, veteran, jèn ak patnè kominotè k ap selebre Selebrasyon 4 Jiyè 250 Ane yo.",
        "july4.cta.watch": "Gade Videyo Selebrasyon yo",
        "july4.cta.submit": "Soumèt Videyo w",

        "install.title": "Enstale ADN Rewards",
        "install.body": "Mete app la sou ekran prensipal ou — pi rapid pou jwenn bon, òf, ak konsyèj AI a.",
        "install.body_ios_a": "Tape",
        "install.body_ios_b": "Pataje",
        "install.body_ios_c": "epi",
        "install.body_ios_d": "Ajoute nan Ekran Prensipal",
        "install.body_ios_e": "pou eksperyans konplè.",
        "install.cta": "Enstale App",

        "common.loading": "Chajman…",
        "common.searching": "Y ap chèche…",
        "common.signin_to": "Konekte pou",
        "common.clear": "Efase",
        "common.all": "Tout",
        "common.cancel": "Anile",
        "common.save": "Sove",
        "common.close": "Fèmen",

        "cookies.title": "Nou itilize kèk cookies.",
        "cookies.body": "Cookies estriktèman nesesè kenbe ou konekte. Sa opsyonèl yo ede nou amelyore ADN. Gade",
        "cookies.privacy_link": "Politik Konfidansyalite",
        "cookies.accept_all": "Aksepte tout",
        "cookies.essential": "Esansyèl sèlman",

        "founding.heading": "Manm VIP Fondatè",
        "founding.limit": "Limite a 50,000",
        "founding.claimed": "deja pran",
        "founding.available": "manm ki rete",
        "founding.lock_rate": "Sere $9.99/mwa pou tout lavi",
        "founding.disclaimer": "Pri VIP Fondatè a sere sèlman pou manm orijinal la epi li pa transferab.",

        "offers.eyebrow": "Ekonomi manm · Jacksonville, FL",
        "offers.title": "Òf Lokal",
        "offers.subtitle": "Òf chwazi nan biznis Jacksonville nan tout katye — soti Riverside rive nan Plaj yo.",
        "offers.search.placeholder": "Chèche pa non biznis, katye, kod postal, kategori…",
        "offers.search.hint": "Rechèch la pa diferansye majiskil — eseye \"riverside\", \"32250\", \"BBQ\", oswa \"San Marco\".",
        "offers.filter.all_neighborhoods": "Tout katye yo",
        "offers.filter.locating": "Y ap jwenn pozisyon w…",
        "offers.filter.use_location": "Itilize pozisyon mwen",
        "offers.filter.sorted_by_distance": "Klase pa distans · Efase",
        "offers.filter.show_map": "Montre kat la",
        "offers.filter.hide_map": "Kache kat la",
        "offers.filter.clear": "Efase filtè yo",
        "offers.count.one": "òf jwenn",
        "offers.count.many": "òf jwenn",
        "offers.empty.title": "Pa gen òf ki koresponn ak filtè w yo",
        "offers.empty.body": "Eseye yon lòt katye, yon kod postal, oswa efase filtè yo.",
        "offers.empty.cta": "Efase filtè yo",
        "offers.card.distance_suffix": "mi",
        "offers.card.250years": "250 Ane",
        "offers.card.redeem": "Itilize",
        "offers.card.signin_to_redeem": "Konekte kòm yon manm aktif pou w itilize.",
        "offers.toast.location_set": "Pozisyon defini — y ap klase pa distans.",
        "offers.toast.location_unsupported": "Navigatè w pa sipòte jeyolokalizasyon.",
        "offers.toast.location_fail": "Pa kapab jwenn pozisyon w.",
        "offers.toast.offer_saved": "Òf sove",

        "marketplace.eyebrow": "Devlopè Sètifye ADN",
        "marketplace.title": "Mache Aplikasyon",
        "marketplace.subtitle": "Aplikasyon verifye ak yon konsantrasyon lokal — soti rezo gadyen jiska kontablite ak sant legliz.",
        "marketplace.loading": "Chajman aplikasyon yo…",
        "marketplace.empty": "Pa gen aplikasyon nan kategori sa a poko.",
        "marketplace.by": "pa",
        "marketplace.install": "Enstale",
        "marketplace.subscribe": "Abònman",
        "marketplace.buy": "Achte",
        "marketplace.signin_to_purchase": "Konekte pou achte.",

        "footer.col.terms": "Tèm",
        "footer.col.privacy": "Konfidansyalite",
        "footer.col.voucher_terms": "Tèm Koupon Otèl",
        "footer.col.video_terms": "Tèm Soumisyon Videyo",
        "footer.col.referral_terms": "Tèm Pwogram Referans",
        "footer.copyright": "Tout dwa rezève.",

        "settings.title": "Fè li pa w.",
        "settings.eyebrow": "Paramèt App",
        "settings.subtitle": "Lang, vwa, aksesibilite, mizajou. Tout sove otomatikman epi swiv ou ant vizit yo.",
        "settings.section.language": "Lang App",
        "settings.section.language.sub": "Itilize nan tout app ak AI Concierge a.",
        "settings.section.updates": "Mizajou ak Vèsyon",
        "settings.section.concierge": "AI Concierge",
        "settings.section.concierge.sub": "Kijan Ask ADN reponn ou.",
        "settings.section.notif": "Notifikasyon",
        "settings.section.notif.sub": "Kijan ADN kenbe w enfòme.",
        "settings.section.display": "Ekran ak Aksesibilite",
        "settings.section.display.sub": "Konfò ak mouvman.",
        "settings.section.install": "Enstale ak App",
        "settings.section.install.sub": "Mete ADN sou ekran prensipal ou.",
        "settings.row.autodetect": "Detekte lang nan premye vizit la",
        "settings.row.autodetect.hint": "Lè li aktive, ADN itilize lang prefere aparèy ou premye fwa. Apre ou chwazi youn anwo a, chwa w toujou genyen.",
        "settings.row.check_updates": "Cheche mizajou kounye a",
        "settings.row.check_updates.hint": "Efase fichye nan cache, jwenn dènye vèsyon an, epi rechaje app la.",
        "settings.row.check_updates.cta": "Cheche mizajou",
        "settings.row.check_updates.busy": "Mizajou…",
        "settings.row.autospeak": "Li repons yo wo",
        "settings.row.autospeak.hint": "Concierge a pale chak repons ak vwa aparèy ou.",
        "settings.row.tts_rate": "Vitès lekti",
        "settings.row.tts_rate.hint": "Konbyen vit repons yo li wo.",
        "settings.row.simple_mode": "Mòd senp",
        "settings.row.simple_mode.hint": "Repons ki klè — fraz kout, san langaj konplike.",
        "settings.row.email_transcripts": "Voye trankripsyon chat la lè m fini yon konvèsasyon",
        "settings.row.clear_chat": "Efase istwa chat sou aparèy sa a",
        "settings.row.clear_chat.hint": "Kòmanse yon konvèsasyon tou nèf. Ansyen trankripsyon w yo rete nan kont ou.",
        "settings.row.clear_chat.cta": "Efase chat",
        "settings.row.browser_notif": "Notifikasyon navigatè",
        "settings.row.browser_notif.hint": "Resevwa yon avi diskrè lè yon òf lokal nouvo parèt oswa fenèt koupon w ouvri. Bezwen pèmisyon ou.",
        "settings.row.browser_notif.enabled": "Aktive",
        "settings.row.browser_notif.enable": "Aktive",
        "settings.row.reduce_motion": "Diminye mouvman",
        "settings.row.reduce_motion.hint": "Dezaktive fondu, paralaks, ak animasyon dekoratif.",
        "settings.row.larger_text": "Tèks pi gwo",
        "settings.row.larger_text.hint": "Ogmante gwosè baz polis 12% nan tout app la.",
        "settings.row.install_status": "Estati enstalasyon",
        "settings.row.install_status.installed": "ADN Rewards enstale kòm yon app sou aparèy sa a.",
        "settings.row.install_status.browser": "Ou ap itilize ADN nan yon tab navigatè. Enstale pou yon eksperyans pi rapid ak plen ekran.",
        "settings.row.install_status.badge_installed": "Enstale",
        "settings.row.install_status.badge_browser": "Navigatè",
        "settings.row.reset_install": "Montre avi enstalasyon ankò",
        "settings.row.reset_install.hint": "Si w te ranvwaye avi a anvan, sa pote l retounen nan pwochen vizit la.",
        "settings.row.reset_install.cta": "Reyajiste avi",
        "settings.tts.slow": "Dousman",
        "settings.tts.normal": "Nòmal",
        "settings.tts.fast": "Vit",
        "settings.privacy_center": "Sant Konfidansyalite",
        "settings.terms_of_service": "Tèm Sèvis",
        "settings.notif.disclaimer": "Nou pa janm voye notifikasyon makèt. Notifikasyon yo rezève pou evènman kont (disponibilite koupon, apwobasyon referans) ak avi òf lokal ou chwazi.",
        "settings.toast.lang_updated": "Lang mete ajou",
        "settings.toast.updates_checking": "Y ap cheche mizajou…",
        "settings.toast.updates_checking.sub": "App la pral rechaje nan yon moman.",
        "settings.toast.chat_cleared": "Istwa chat efase sou aparèy sa a",
        "settings.toast.install_reset": "Avi enstalasyon an ap parèt ankò nan pwochen vizit ou",
        "settings.toast.notif_enabled": "Notifikasyon aktive",
        "settings.toast.notif_not_enabled": "Notifikasyon yo pa aktive",
        "settings.toast.notif_not_enabled.sub": "Ou ka chanje sa nan paramèt navigatè w.",
        "settings.toast.notif_unsupported": "Notifikasyon pa sipòte sou aparèy sa a",
        "settings.confirm.clear_chat": "Fini konvèsasyon aktyèl la epi efase istwa chat sou aparèy sa a?",

        "member.eyebrow": "Tablo Manm",
        "member.welcome": "Byenveni,",
        "member.member_id": "ID Manm:",
        "member.badge.founding": "Founding VIP",
        "member.badge.lifetime": "Pou tout lavi $9.99/mwa",
        "member.badge.active": "Aktif",
        "member.badge.inactive": "Pa aktif",
        "member.cta.activate": "Aktive Manm ($25)",
        "member.cta.settings": "Paramèt",

        "faq.q1": "Èske sa a se yon envestisman?",
        "faq.a1": "Non. ADN Local Rewards & Loyalty Network™ se yon platfòm peye pou manm ak rekonpans. Pa gen pwopriyete, pataj benefis, valè, kapital, oswa revni pasif.",
        "faq.q2": "Kisa $25 yo kouvri?",
        "faq.a2": "Aktivasyon Founding VIP $25 yon sèl fwa a sere to vitalis $9.99/mwa w, kòmanse benefis ou yo, epi bay koupon otèl $100 w.",
        "faq.q3": "Èske m ka transfere manm mwen?",
        "faq.a3": "Non. Pri Founding VIP a bloke sèlman pou manm orijinal la epi li pa transferab.",
        "faq.q4": "Konbyen tan pri Founding VIP a disponib?",
        "faq.a4": "Pri Founding VIP rezève pou premye 50,000 manm yo. Lè limit sa a atenn, pri estanda aplike.",
        "faq.q5": "Kisa ki rive si m anile?",
        "faq.a5": "Ou ka anile nenpòt lè soti nan tablo manm ou epi w ap kenbe aksè konplè pandan yon peryòd gras 7 jou. Apre peryòd la, manm ou ap pòz.",
        "faq.q6": "E si peman m echwe?",
        "faq.a6": "Nou pa sispann ou imedyatman. Ou antre nan yon fenèt gras pou mete kat ou ajou. Apre peryòd la, Core ADN Rule a pòz pwofil biznis ou.",
        "faq.q7": "Kijan koupon otèl la fonksyone?",
        "faq.a7": "Apre aktivasyon, w ap resevwa yon kòd koupon $100 inik nan tablo manm ou. Itilize ak patnè apwouve dapre tèm koupon yo.",
        "faq.q8": "Kiyès ki ka soumèt yon videyo selebrasyon?",
        "faq.a8": "Manm aktif yo ka soumèt yon videyo pou revizyon. Tout videyo yo dwe apwouve epi respekte dwa otè.",
        "faq.q9": "Kisa òf Founding Business Partner an ye?",
        "faq.a9": "Premye 100 biznis lokal ki rantre nan ADN jwenn estati Founding Business Partner™ — $0/mwa pou tout tan, pozisyon priyorite sou kat la, ak yon òf aktif bloke pou tout lavi.",
        "faq.q10": "Poukisa mèt biznis ak devlopè bezwen yon manm?",
        "faq.a10": "ADN se yon kominote premye pou manm. Core ADN Rule a egzije chak mèt biznis, devlopè sètifye, ak anbasadè kenbe yon manm ADN aktif.",
        "faq.q11": "Ki aplikasyon ki nan mache a?",
        "faq.a11": "Aplikasyon verifye ak yon konsantrasyon lokal soti nan devlopè ADN Certified — sèvis, zouti biznis, AI, edikasyon, byen imobilye, aplikasyon legliz, ak plis.",
        "faq.q12": "Kijan pou m vin yon ADN Certified Developer?",
        "faq.a12": "Nenpòt moun ka aplike. Enskri nan kourikoulòm 12 semèn ADN AI App Certification an. Sèlman 100 sètifikasyon disponib nan pri lansman $999 (pri regilye $4,999). Bous disponib. Manm ADN aktif obligatwa.",
        "faq.q13": "Kijan referans ak pwogram Anbasadè yo fonksyone?",
        "faq.a13": "Chak manm aktif jwenn yon lyen referans inik. Ou genyen rekonpans pou chak zanmi ki aktive, jiska 12 mwa nan kredi, epi ak 25 referans siksè to $9.99/mwa w vin gratis pou tout lavi.",
        "faq.q14": "Èske m ka jwenn òf toupre m?",
        "faq.a14": "Wi. Sou paj Òf Lokal yo, tape \"Itilize pozisyon mwen\" pou klase biznis pa distans. Ou ka filtre pa katye, kategori, oswa chèche pa non oswa kod postal.",
        "faq.q15": "Ki kote ADN opere?",
        "faq.a15": "N ap lanse nan Jacksonville, FL ak selebrasyon America 250 4 Jiyè 2026 la. Nouvo vil fondatè yo ap ajoute baze sou demand manm yo.",
        "faq.q16": "Èske enfòmasyon mwen an sekirite?",
        "faq.a16": "Wi. ADN itilize otantifikasyon kripte, pa janm vann done pèsonèl ou, epi pataje sèlman minimòm ki nesesè a. Detay konplè nan politik Konfidansyalite a.",
        "faq.q17": "Kijan m jwenn èd?",
        "faq.a17": "Kontakte sipò dirèkteman soti nan tablo manm ou oswa atravè lyen kontak nan pye paj la. Manm Founding VIP yo jwenn repons priyorite.",
        "faq.q18": "Kisa ADN ye?",
        "faq.a18": "ADN — ki vle di Axxess Denied Network — se yon mache enpak kominote ki baze nan Jacksonville. Nou ede biznis lokal yo grandi nan konekte yo dirèkteman ak manm ki vle sipòte katye yo.",
        "faq.q19": "Kijan m jwenn kredi ADN?",
        "faq.a19": "Manm yo jwenn kredi ADN chak fwa yo itilize yon òf lokal, refere yon zanmi, oswa kontribye nan yon kanpay kominote. Kredi yo bay diskont anplis, bon otèl ak avantaj eksklizif.",
        "faq.q20": "Kijan m ka sipòte yon biznis lokal?",
        "faq.a20": "Vin yon manm, itilize òf yo, pataje pwofil yo ak vwazen ou, epi kite revizyon onèt. Chak itilizasyon voye revni reyèl dirèkteman bay biznis Jacksonville la — pa gen entèmedyè.",
    },
};

function detectInitialLang() {
    if (typeof window === "undefined") return "en";
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && RESOURCES[stored]) return stored;
    const nav = (navigator.language || "en").toLowerCase().split("-")[0];
    return RESOURCES[nav] ? nav : "en";
}

export function getLang() {
    return detectInitialLang();
}

export function setLang(code) {
    if (typeof window === "undefined") return;
    if (!RESOURCES[code]) code = "en";
    localStorage.setItem(STORAGE_KEY, code);
    // Keep the chat language preference in sync (matches Concierge dropdown codes)
    const chatCode = {
        en: "en-US", es: "es-US", pt: "pt-BR", fr: "fr-CA", ht: "ht-HT",
    }[code] || "en-US";
    try { localStorage.setItem("adn_chat_lang", chatCode); } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent("adn-lang-change", { detail: { code } }));
}

export function tr(key, code) {
    const lc = code || detectInitialLang();
    const dict = RESOURCES[lc] || RESOURCES.en;
    return dict[key] !== undefined ? dict[key] : (RESOURCES.en[key] !== undefined ? RESOURCES.en[key] : key);
}

export function useI18n() {
    const [lang, setLangState] = useState(detectInitialLang);
    useEffect(() => {
        const handler = (e) => setLangState(e.detail?.code || detectInitialLang());
        window.addEventListener(EVENT_NAME, handler);
        return () => window.removeEventListener(EVENT_NAME, handler);
    }, []);
    const t = useCallback((key) => tr(key, lang), [lang]);
    return { t, lang, setLang, LANGS };
}
